// Serviço de API para comunicação com o backend
// Em desenvolvimento usa /api (Vite proxy para o backend em localhost:3001), evitando CORS.
import { clearActiveAuthStorage } from '@/lib/session-utils';
import { comprimirImagem, comprimirImagens } from '@/utils/comprimir-imagem';
import type { IaJob, OverrideContextoIA, PreviewIA, StatusIaPorPasta } from '@/types/apreciacao-ia';
import type {
  AnalisePdfRetorno,
  ChecklistRevisao,
  IndicadoresRevisao,
  RevisaoDetalhe,
  RevisaoResumo,
} from '@/types/revisao';

export type { IaJob, OverrideContextoIA, PreviewIA, StatusIaPorPasta };

const getApiBaseUrl = () => {
  const envUrl =
    import.meta.env.VITE_API_URL ??
    '/api';

  // Substituir 0.0.0.0 por localhost (0.0.0.0 não funciona no navegador)
  let normalizedUrl = envUrl.replace(/0\.0\.0\.0/g, 'localhost');

  // Para URLs de produção (não localhost ou IPs privados), forçar HTTPS
  const isPrivateNetwork = /192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+|172\.(1[6-9]|2[0-9]|3[0-1])\.\d+\.\d+/.test(normalizedUrl);
  if (normalizedUrl.startsWith('http://') && !normalizedUrl.includes('localhost') && !normalizedUrl.includes('127.0.0.1') && !isPrivateNetwork) {
    normalizedUrl = normalizedUrl.replace('http://', 'https://');
  }

  return normalizedUrl;
};

const API_BASE_URL = getApiBaseUrl();

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/** Veredito da revisão manual de uma foto (acervo legado). */
export type RevisaoFotoStatus = 'aprovada' | 'reprovada';

export interface RevisaoFoto {
  status: RevisaoFotoStatus;
  /** Por que foi reprovada. */
  motivo?: string;
  revisadoPor?: string;
  revisadoEm: string;
}

/**
 * Como a data de levantamento foi obtida, da mais confiável para a menos:
 * `captura` = carimbo do app no momento da foto; `upload` = quando a primeira
 * foto chegou ao servidor; `inferida` = mtime do arquivo mais antigo;
 * `importacao` = mtime numa pasta de acervo importado, ou seja, a data do
 * import e não a do levantamento.
 */
export type OrigemDataLevantamento = 'captura' | 'upload' | 'inferida' | 'importacao';

export interface UploadedFoto {
  nomeArquivo: string;
  tamanho: number;
  dataModificacao: string;
  /** Quando a foto foi tirada em campo (ISO). Só existe em foto vinda do app. */
  dataCaptura?: string;
  url: string;
  previewUrl: string;
  /** `video` abre o player com captura de quadro no lugar do visualizador de imagem. */
  tipo?: 'imagem' | 'video';
  /** Aprovação/reprovação manual (ausente = pendente de revisão). */
  revisao?: RevisaoFoto;
  /** Descrição gerada pela IA (presente quando a foto já foi analisada). */
  descricaoIA?: string;
  /** Rótulo curto gerado pela IA. */
  rotuloIA?: string;
  /** Quando a foto foi analisada pela IA (ISO). */
  analisadoEmIA?: string;
}

export interface UploadedMachineFolder {
  machineFolder: string;
  machineName: string;
  /** Total de itens da pasta (imagens + vídeos). */
  totalFotos: number;
  /** Quantos desses itens são vídeo. */
  totalVideos?: number;
  lastModifiedAt: string | null;
  /**
   * Quando a máquina foi levantada em campo (ISO) — a foto mais ANTIGA da
   * pasta, ao contrário de `lastModifiedAt`. Leia junto com a origem: só
   * `captura` e `upload` são registros; o resto é estimativa.
   */
  dataLevantamento?: string | null;
  origemDataLevantamento?: OrigemDataLevantamento | null;
  fotos: UploadedFoto[];
  area?: string | null;
  /** Status manual da pasta (sem_foto_maquina, sem_foto_painel, incompleto, cancelado). */
  status?: string | null;
  /** Pasta do acervo legado: fica na aba "Legado" e passa pela revisão foto a foto. */
  legado?: boolean;
  /** Primeiro vínculo (retrocompat). Use `apreciacaoIds` para a lista completa. */
  apreciacaoId?: string | null;
  /** Vínculos N:N: a pasta pode pertencer a várias apreciações. */
  apreciacaoIds?: string[];
  maquinaId?: string | null;
}

export interface UploadedFotosResponse {
  maquinas: UploadedMachineFolder[];
  legado: UploadedMachineFolder[];
}

/**
 * Etapa 1 da importação: o que o ZIP criaria, sem nada gravado ainda.
 * Vale até ser confirmada (o servidor descarta ZIPs pendentes em 2h).
 */
export interface AnaliseZip {
  token: string;
  legado: boolean;
  pastas: Array<{ pasta: string; origem: string; fotos: number }>;
  totalFotos: number;
  ignorados: number;
  conflitos: string[];
}

/** Resumo do que entrou (e do que ficou de fora) na importação de um ZIP. */
export interface ImportacaoZipResultado {
  legado: boolean;
  pastas: number;
  pastasNovas: number;
  fotos: number;
  /** Arquivos do ZIP que não eram imagem. */
  ignorados: number;
  /** Pastas puladas por já existirem no outro acervo. */
  conflitos: string[];
}

export interface EncaixeParteSugestao {
  parte: string;
  fotos: Array<{ url: string; rotulo: string }>;
  sugestoes: {
    descricaoCondicaoAtual: string;
    perigosEncontrados: string;
    consequencias: string;
    eventoPerigoso: string;
  };
}

export interface EncaixeFotosResultado {
  partes: EncaixeParteSugestao[];
  totalFotos: number;
}

export interface RelatorioMensalResponse {
  periodo: {
    ano: number;
    mes: number;
    inicioIso: string;
    fimIso: string;
    diasNoMes: number;
    label: string;
  };
  kpis: {
    planejado: number;
    executado: number;
    desvio: number;
    solicitacoesNaoAtendidas: number;
    taxaExecucao: number;
  };
  curvaS: Array<{
    dia: number;
    data: string;
    planejadoAcumulado: number;
    executadoAcumulado: number;
    desvioAcumulado: number;
    planejadoAntigoAcumulado?: number;
    hoje?: boolean;
  }>;
  temCronogramaAntigo?: boolean;
  docsRetornoAtrasados: {
    totalPendencias: number;
    totalAtrasadas: number;
    totalSemVinculo: number;
    itens: Array<{
      tarefaId: string;
      nomeTarefa: string;
      nivel: number;
      area: string | null;
      local: string | null;
      semana: string | null;
      status: 'atrasada' | 'pendente_vinculo' | 'pendente_execucao';
      motivo: string;
      terminoPlanejado: string | null;
      percentualEfetivo: number;
    }>;
  };
  statusAreas: {
    totalAreas: number;
    itens: Array<{
      area: string;
      total: number;
      status: Array<{ status: string; quantidade: number }>;
    }>;
  };
  estratificacaoHrn: {
    totalRiscos: number;
    totalConformes: number;
    totalNaoConformes: number;
    aceitavel: number;
    muito_baixo: number;
    baixo: number;
    significante: number;
    alto: number;
    muito_alto: number;
    extremo: number;
    inaceitavel: number;
    itensPorMaquina: Array<{
      maquinaId: string;
      maquinaNome: string;
      partes: Array<{
        parte: string;
        riscos: Array<{
          hrnAtual: number;
          classificacao:
            | 'aceitavel'
            | 'muito_baixo'
            | 'baixo'
            | 'significante'
            | 'alto'
            | 'muito_alto'
            | 'extremo'
            | 'inaceitavel';
          conformeAtual: boolean;
        }>;
      }>;
    }>;
  };
  recursosAlocados: {
    total: number;
    porOrigem: Array<{ origem: string; quantidade: number }>;
  };
  cronogramaDetalhado: Array<CronogramaTaskResponse & { estado?: string }>;
}

export interface CronogramaTaskResponse {
  id: string;
  parentId: string | null;
  ordem: number;
  nivel: number;
  resumo: boolean;
  nomeTarefa: string;
  sismanaAntigo: string | null;
  area: string | null;
  local: string | null;
  semana: string | null;
  mes: string | null;
  duracaoRaw: string | null;
  duracaoDias: number | null;
  predecessoras: string | null;
  inicioPlanejado: string | null;
  terminoPlanejado: string | null;
  percentualOrigem: number | null;
  percentualOverride: number | null;
  percentualCalculado: number;
  percentualEfetivo: number;
  dataConclusao: string | null;
  tipoEtapa: 'levantamento' | 'emissao' | 'tramite_aprovacao' | 'resumo' | 'revisao' | 'outra';
  statusVinculo: 'vinculado' | 'pendente' | 'ambiguo';
  estrategiaVinculo: string | null;
  pendenciaVinculo: string | null;
  statusExecucao: 'concluida' | 'atrasada' | 'pendente_vinculo' | 'em_andamento' | 'pendente';
  ativoId: string | null;
}

export interface CronogramaListResponse {
  possuiCronograma: boolean;
  cronogramaProjeto: {
    id: string;
    importadoEm: string;
    arquivoOrigem: string;
    totalTarefas: number;
    periodoInicioIso: string | null;
    periodoFimIso: string | null;
  } | null;
  resumo: {
    total: number;
    concluidas: number;
    atrasadas: number;
    pendentesVinculo: number;
    emAndamento: number;
    pendentes: number;
  };
  tarefas: CronogramaTaskResponse[];
}

export interface CronogramaRevisaoResponse {
  id: string;
  tarefaId: string;
  tarefaNome: string;
  campo: string;
  valorAnterior: unknown;
  valorNovo: unknown;
  usuarioId: string | null;
  usuarioNome: string | null;
  createdAt: string;
}

/**
 * Lê o nome do arquivo do Content-Disposition da resposta.
 * `filename*` (RFC 5987) vem primeiro por preservar acentos.
 *
 * Só funciona porque o backend expõe Content-Disposition no CORS.
 */
function extrairNomeArquivoContentDisposition(
  contentDisposition: string | null
): string | undefined {
  if (!contentDisposition) return undefined;

  const estendido = contentDisposition.match(/filename\*\s*=\s*[^']*'[^']*'([^;]+)/i);
  if (estendido) {
    try {
      const nome = decodeURIComponent(estendido[1].trim()).trim();
      if (nome) return nome;
    } catch {
      // percent-encoding quebrado: cai no filename simples
    }
  }

  const simples = contentDisposition.match(/filename\s*=\s*("([^"]*)"|[^;]+)/i);
  const nome = (simples?.[2] ?? simples?.[1] ?? '').trim();
  return nome || undefined;
}

/** Filtros aceitos pelas rotas de carteira. Campo vazio é omitido da query. */
export interface FiltrosCarteira {
  importacaoId?: string;
  squad?: string;
  situacao?: string;
  famComl?: string;
  regiao?: string;
  busca?: string;
  apenasComSaldo?: boolean;
}

function queryCarteira<T extends object>(filtros?: T): string {
  const query = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtros ?? {})) {
    if (valor === undefined || valor === null || valor === '' || valor === false) continue;
    query.set(chave, String(valor));
  }
  const s = query.toString();
  return s ? `?${s}` : '';
}

/**
 * Toda chamada à API passa por aqui.
 *
 * O JWT deixou de morar no localStorage e virou cookie HttpOnly (RG-05): o
 * JavaScript não o enxerga mais, e o navegador só o envia quando a requisição
 * declara `credentials: 'include'`. Sem esta função, cada ponto de chamada
 * teria de lembrar disso — e o que esquecesse sairia anônimo, com 401.
 */
function fetchApi(entrada: string, init: RequestInit = {}): Promise<Response> {
  return fetch(entrada, { ...init, credentials: 'include' });
}

class ApiService {
  /** Renovação em curso, para que várias chamadas em paralelo não disparem N refresh. */
  private renovacaoEmCurso: Promise<boolean> | null = null;

  private invalidateActiveSession(options?: { redirectToLogin?: boolean; reason?: string }) {
    clearActiveAuthStorage();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:session-invalidated', {
        detail: { reason: options?.reason || 'unauthorized' }
      }));
      if (options?.redirectToLogin && !window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
  }

  /**
   * Limpa o objeto RequestInit removendo propriedades undefined e inválidas
   * Retorna apenas propriedades essenciais e válidas para evitar conflitos
   */
  private cleanRequestInit(options: RequestInit): Partial<RequestInit> {
    const cleaned: Partial<RequestInit> = {};

    // Apenas incluir propriedades válidas e essenciais que não são undefined
    // Não incluir headers aqui, pois são tratados separadamente
    if (options.method !== undefined && typeof options.method === 'string') {
      cleaned.method = options.method;
    }
    if (options.body !== undefined) {
      cleaned.body = options.body;
    }
    if (options.mode !== undefined && typeof options.mode === 'string') {
      cleaned.mode = options.mode;
    }
    if (options.credentials !== undefined && typeof options.credentials === 'string') {
      cleaned.credentials = options.credentials;
    }
    if (options.cache !== undefined && typeof options.cache === 'string') {
      cleaned.cache = options.cache;
    }
    if (options.redirect !== undefined && typeof options.redirect === 'string') {
      cleaned.redirect = options.redirect;
    }
    if (options.referrerPolicy !== undefined && typeof options.referrerPolicy === 'string') {
      cleaned.referrerPolicy = options.referrerPolicy;
    }
    if (options.integrity !== undefined && typeof options.integrity === 'string') {
      cleaned.integrity = options.integrity;
    }
    if (options.keepalive !== undefined && typeof options.keepalive === 'boolean') {
      cleaned.keepalive = options.keepalive;
    }
    if (options.signal !== undefined && options.signal instanceof AbortSignal) {
      cleaned.signal = options.signal;
    }

    return cleaned;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    jaRenovou = false
  ): Promise<ApiResponse<T>> {
    try {
      // Validar endpoint
      if (!endpoint || typeof endpoint !== 'string') {
        throw new Error('Endpoint inválido');
      }

      // Validar e construir URL
      const urlString = `${API_BASE_URL}${endpoint}`;

      // Garantir que a URL seja uma string válida
      if (typeof urlString !== 'string' || urlString.trim() === '') {
        throw new Error('URL inválida');
      }

      // Aceitar URLs absolutas (http/https) e relativas de mesma origem (/api/...)
      if (
        !urlString.startsWith('http://') &&
        !urlString.startsWith('https://') &&
        !urlString.startsWith('/')
      ) {
        throw new Error(`URL inválida: use http(s):// ou caminho relativo iniciado por "/". Recebido: ${urlString}`);
      }

      const url = urlString;

      // Log para debug (apenas em desenvolvimento)
      if (import.meta.env.DEV) {
        console.log(`[API] Request: ${options.method || 'GET'} ${url}`);
        console.log(`[API] API_BASE_URL: ${API_BASE_URL}`);
        console.log(`[API] Endpoint: ${endpoint}`);
      }

      // Construir headers de forma segura como objeto plano
      const headers: Record<string, string> = {};

      // Mesclar headers do options se existirem (apenas se forem um objeto válido)
      if (options.headers) {
        if (options.headers instanceof Headers) {
          // Se for um objeto Headers, converter para objeto plano
          options.headers.forEach((value, key) => {
            headers[key] = value;
          });
        } else if (typeof options.headers === 'object' && !Array.isArray(options.headers)) {
          // Se for um objeto plano, mesclar apenas strings válidas
          Object.entries(options.headers).forEach(([key, value]) => {
            if (typeof key === 'string' && typeof value === 'string') {
              headers[key] = value;
            }
          });
        }
      }


      // Só enviar Content-Type quando houver body JSON/string.
      // Evita erro no backend para DELETE sem body.
      const hasBody = options.body !== undefined && options.body !== null;
      if (hasBody && !(options.body instanceof FormData)) {
        if (!Object.keys(headers).some((k) => k.toLowerCase() === 'content-type')) {
          headers['Content-Type'] = 'application/json';
        }
      } else {
        // Se não há body ou body é FormData, garantir que não forçamos content-type.
        Object.keys(headers).forEach((k) => {
          if (k.toLowerCase() === 'content-type') {
            delete headers[k];
          }
        });
      }

      // Limpar opções do fetch removendo propriedades undefined e headers
      const cleanedOptions = this.cleanRequestInit(options);

      // Construir opções do fetch de forma explícita e segura
      // Não fazer spread direto para evitar conflitos com propriedades somente leitura
      const fetchOptions: RequestInit = {
        method: cleanedOptions.method || 'GET',
        mode: cleanedOptions.mode || 'cors',
        headers: headers,
      };

      // Adicionar credentials apenas se estiver definido e validado
      if (cleanedOptions.credentials !== undefined) {
        fetchOptions.credentials = cleanedOptions.credentials;
      }

      // Adicionar propriedades opcionais apenas se estiverem definidas
      if (cleanedOptions.body !== undefined) {
        fetchOptions.body = cleanedOptions.body;
      }
      if (cleanedOptions.cache !== undefined) {
        fetchOptions.cache = cleanedOptions.cache;
      }
      if (cleanedOptions.redirect !== undefined) {
        fetchOptions.redirect = cleanedOptions.redirect;
      }
      if (cleanedOptions.referrerPolicy !== undefined) {
        fetchOptions.referrerPolicy = cleanedOptions.referrerPolicy;
      }
      if (cleanedOptions.integrity !== undefined) {
        fetchOptions.integrity = cleanedOptions.integrity;
      }
      if (cleanedOptions.keepalive !== undefined) {
        fetchOptions.keepalive = cleanedOptions.keepalive;
      }
      if (cleanedOptions.signal !== undefined) {
        fetchOptions.signal = cleanedOptions.signal;
      }

      // Log das opções antes de fazer o fetch (apenas em desenvolvimento)
      if (import.meta.env.DEV) {
        console.log('[API] Fetch Options:', {
          url,
          method: fetchOptions.method,
          hasBody: !!fetchOptions.body,
          headers: fetchOptions.headers
        });
      }

      // Garantir que fetch seja chamado com parâmetros válidos
      let response: Response;
      try {
        // Validar que url é uma string antes de passar para fetch
        if (typeof url !== 'string') {
          throw new Error(`URL deve ser uma string, recebido: ${typeof url}`);
        }

        // Validar que fetchOptions não contém propriedades inválidas
        const safeOptions: RequestInit = {
          method: fetchOptions.method,
          headers: fetchOptions.headers,
          mode: fetchOptions.mode,
        };

        if (fetchOptions.body !== undefined) {
          safeOptions.body = fetchOptions.body;
        }
        if (fetchOptions.credentials !== undefined) {
          safeOptions.credentials = fetchOptions.credentials;
        }
        if (fetchOptions.cache !== undefined) {
          safeOptions.cache = fetchOptions.cache;
        }
        if (fetchOptions.redirect !== undefined) {
          safeOptions.redirect = fetchOptions.redirect;
        }
        if (fetchOptions.referrerPolicy !== undefined) {
          safeOptions.referrerPolicy = fetchOptions.referrerPolicy;
        }
        if (fetchOptions.integrity !== undefined) {
          safeOptions.integrity = fetchOptions.integrity;
        }
        if (fetchOptions.keepalive !== undefined) {
          safeOptions.keepalive = fetchOptions.keepalive;
        }
        if (fetchOptions.signal !== undefined) {
          safeOptions.signal = fetchOptions.signal;
        }

        response = await fetchApi(url, safeOptions);
      } catch (fetchError) {
        if (import.meta.env.DEV) {
          console.error('[API] Erro ao executar fetch:', fetchError);
        }
        throw fetchError;
      }

      // Log da resposta (apenas em desenvolvimento)
      if (import.meta.env.DEV) {
        console.log('[API] Response:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok
        });
      }

      // Tentar fazer parse do JSON, mas tratar erros caso a resposta não seja JSON
      let data: any;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
          // Log da resposta JSON (apenas em desenvolvimento)
          if (import.meta.env.DEV) {
            console.log('[API] Response Data:', data);
          }
        } catch (jsonError) {
          // Se não conseguir fazer parse do JSON, criar um objeto de erro
          if (import.meta.env.DEV) {
            console.error('[API] Erro ao fazer parse do JSON:', jsonError);
          }
          data = {
            success: false,
            message: 'Erro ao processar resposta do servidor',
            errors: ['Resposta inválida do servidor']
          };
        }
      } else {
        // Se não for JSON, criar um objeto de erro
        const text = await response.text();
        if (import.meta.env.DEV) {
          console.log('[API] Response Text (não JSON):', text);
        }
        data = {
          success: false,
          message: text || 'Erro no servidor',
          errors: [text || 'Erro desconhecido']
        };
      }

      // 401 virou rotina: o token de acesso dura cerca de uma hora. Antes de
      // derrubar a sessão, tenta renovar pelo refresh (cookie) e repetir a
      // requisição uma única vez.
      if (response.status === 401) {
        const rotaDeSessao = endpoint.startsWith('/auth/');
        if (!jaRenovou && !rotaDeSessao) {
          const renovou = await this.renovarSessao();
          if (renovou) {
            return this.request<T>(endpoint, options, true);
          }
        }

        this.invalidateActiveSession({ reason: 'unauthorized' });

        // Log para debug
        if (import.meta.env.DEV) {
          console.warn('[API] Sessão inválida ou expirada. Limpando autenticação.');
        }

        return {
          success: false,
          message: data.message || 'Não autorizado',
          errors: data.errors || ['Não autorizado']
        };
      }

      // Se a resposta não foi bem-sucedida, garantir que temos uma mensagem de erro
      if (!response.ok) {
        return {
          success: false,
          message: data.message || `Erro ${response.status}: ${response.statusText}`,
          errors: data.errors || [data.message || `Erro ${response.status}: ${response.statusText}`]
        };
      }

      return data;
    } catch (error) {
      // Log detalhado do erro (apenas em desenvolvimento)
      if (import.meta.env.DEV) {
        console.error('[API] Erro na requisição:', error);
        console.error('[API] Stack:', error instanceof Error ? error.stack : 'N/A');
      }

      return {
        success: false,
        message: 'Erro ao conectar com o servidor',
        errors: [error instanceof Error ? error.message : 'Erro desconhecido'],
      };
    }
  }

  // Apreciações
  async listarApreciacoes(params?: {
    page?: number;
    limit?: number;
    status?: string;
    maquinaId?: string;
    busca?: string;
    projetoId?: string;
    guiaId?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.maquinaId) queryParams.append('maquinaId', params.maquinaId);
    if (params?.busca) queryParams.append('busca', params.busca);
    if (params?.projetoId) queryParams.append('projetoId', params.projetoId);
    if (params?.guiaId) queryParams.append('guiaId', params.guiaId);

    const query = queryParams.toString();
    return this.request(`/apreciacoes${query ? `?${query}` : ''}`);
  }

  async exportarTodasApreciacoes(): Promise<ApiResponse> {
    return this.request('/apreciacoes/exportar-todas');
  }

  async buscarApreciacao(id: string): Promise<ApiResponse> {
    return this.request(`/apreciacoes/${id}`);
  }

  /**
   * Retorna apenas os campos de imagem dos riscos (para merge após buscarApreciacao).
   * Chave: id do risco. Valor: { fotoAtualUrl1?, fotoAtualUrl2?, fotoAtualUrl3?, fotoAtualUrl4?, fotoPlrUrl? }.
   */
  async buscarRiscosImagens(apreciacaoId: string, quality: 'low' | 'high' = 'low'): Promise<
    ApiResponse<Record<string, { fotoAtualUrl1?: string | null; fotoAtualUrl2?: string | null; fotoAtualUrl3?: string | null; fotoAtualUrl4?: string | null; fotoPlrUrl?: string | null }>>
  > {
    return this.request(`/apreciacoes/${apreciacaoId}/riscos-imagens?quality=${quality}`);
  }

  async criarApreciacao(dados: any): Promise<ApiResponse> {
    return this.request('/apreciacoes', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarApreciacao(id: string, dados: any): Promise<ApiResponse> {
    return this.request(`/apreciacoes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerApreciacao(id: string): Promise<ApiResponse> {
    return this.request(`/apreciacoes/${id}`, {
      method: 'DELETE',
    });
  }

  // Copia todos os campos de cabeçalho/identificação/executantes/máquina
  // de outra apreciação (origemId) para a apreciação atual (destId).
  // Não copia riscos, id, numero ou projetoId.
  async copiarCabecalhoDeApreciacao(destId: string, origemId: string): Promise<ApiResponse> {
    return this.request(`/apreciacoes/${destId}/copiar-cabecalho`, {
      method: 'POST',
      body: JSON.stringify({ origemId }),
    });
  }

  // Gera partes automaticamente com IA (3 etapas: descrever → agrupar → detalhar).
  // As partes geradas são acrescentadas às existentes.
  async gerarPartesComIA(
    apreciacaoId: string,
    imagens: Array<{ base64: string; fileName?: string }>
  ): Promise<ApiResponse<{
    descricoes: Array<{ rotulo: string; descricao: string }>;
    grupos: Array<{ parteNome: string; imagensIndices: number[] }>;
    riscos: any[];
    erros?: Array<{ parte: string; erro: string }>;
  }>> {
    return this.request(`/ai/gerar-partes/${apreciacaoId}`, {
      method: 'POST',
      body: JSON.stringify({ imagens }),
    });
  }

  // Pipeline granular (evita 504 do nginx): 1 chamada por imagem / grupo.
  async aiDescreverImagem(
    apreciacaoId: string,
    base64: string,
    fileName?: string
  ): Promise<ApiResponse<{ rotulo: string; descricao: string; url: string }>> {
    return this.request(`/ai/gerar-partes/${apreciacaoId}/descrever-imagem`, {
      method: 'POST',
      body: JSON.stringify({ base64, fileName }),
    });
  }

  // Lote: descreve várias imagens (recomendado até 5) numa única requisição.
  // Retorna uma descrição por imagem, na mesma ordem das imagens enviadas.
  async aiDescreverImagens(
    apreciacaoId: string,
    imagens: Array<{ base64: string; fileName?: string }>
  ): Promise<ApiResponse<Array<{ rotulo: string; descricao: string; url: string }>>> {
    return this.request(`/ai/gerar-partes/${apreciacaoId}/descrever-imagens`, {
      method: 'POST',
      body: JSON.stringify({ imagens }),
    });
  }

  async aiAgruparPartes(
    apreciacaoId: string,
    descricoes: Array<{ rotulo: string; descricao: string }>
  ): Promise<ApiResponse<{ grupos: Array<{ parteNome: string; imagensIndices: number[] }> }>> {
    return this.request(`/ai/gerar-partes/${apreciacaoId}/agrupar`, {
      method: 'POST',
      body: JSON.stringify({ descricoes }),
    });
  }

  async aiDetalharParte(
    apreciacaoId: string,
    payload: { parteNome: string; imagensBase64: string[]; urls: string[]; descricoes?: string[]; numero?: number; ordem?: number }
  ): Promise<ApiResponse<{ risco: any }>> {
    return this.request(`/ai/gerar-partes/${apreciacaoId}/detalhar-parte`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async aiChat(
    pergunta: string,
    historico: { role: 'user' | 'model'; text: string }[] = []
  ): Promise<ApiResponse<{ resposta: string }>> {
    return this.request(`/ai/chat`, {
      method: 'POST',
      body: JSON.stringify({ pergunta, historico }),
    });
  }

  async aiChatNormas(
    pergunta: string,
    historico: { role: 'user' | 'model'; text: string }[] = []
  ): Promise<ApiResponse<{ resposta: string }>> {
    return this.request(`/ai/chat-normas`, {
      method: 'POST',
      body: JSON.stringify({ pergunta, historico }),
    });
  }

  async aiRedetalharParte(
    apreciacaoId: string,
    riscoId: string,
    imagensBase64?: string[]
  ): Promise<ApiResponse<{ risco: any }>> {
    return this.request(`/ai/gerar-partes/${apreciacaoId}/redetalhar-parte`, {
      method: 'POST',
      body: JSON.stringify({ riscoId, imagensBase64 }),
    });
  }

  // Riscos
  async adicionarRisco(apreciacaoId: string, dados: any): Promise<ApiResponse> {
    return this.request(`/apreciacoes/${apreciacaoId}/riscos`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarRisco(riscoId: string, dados: any): Promise<ApiResponse> {
    return this.request(`/riscos/${riscoId}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async reordenarRiscos(apreciacaoId: string, ordem: Array<{ id: string; numero: number }>): Promise<ApiResponse> {
    return this.request(`/apreciacoes/${apreciacaoId}/riscos/reordenar`, {
      method: 'PUT',
      body: JSON.stringify({ ordem }),
    });
  }

  async removerRisco(riscoId: string): Promise<ApiResponse> {
    return this.request(`/riscos/${riscoId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Salva o PDF gerado no servidor para o portal do cliente
   */
  async salvarPdfApreciacao(apreciacaoId: string, pdfBase64: string): Promise<ApiResponse> {
    return this.request(`/apreciacoes/${apreciacaoId}/salvar-pdf`, {
      method: 'POST',
      body: JSON.stringify({ pdf: pdfBase64 }),
    });
  }

  /**
   * Exporta a apreciação como PDF a partir do LaTeX
   */
  async exportarPdf(
    apreciacaoId: string,
    latex: string,
    options?: { mode?: 'preview' | 'final'; signal?: AbortSignal }
  ): Promise<Blob> {
    const url = `${API_BASE_URL}/apreciacoes/${apreciacaoId}/exportar-pdf`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };


    const response = await fetchApi(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ latex, mode: options?.mode }),
      mode: 'cors',
      credentials: 'include',
      signal: options?.signal,
    });

    // Se for 401 (não autorizado), apenas limpar token
    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      const errorMessage = error.message || 'Erro ao exportar PDF';
      const errorDetails = error.log || error.stderr || error.error || '';
      const fullMessage = errorDetails
        ? `${errorMessage}\n\nDetalhes:\n${errorDetails.substring(0, 1000)}`
        : errorMessage;
      throw new Error(fullMessage);
    }

    return await response.blob();
  }

  /**
   * Exporta a apreciação como Word (.docx) gerado diretamente dos dados
   */
  async exportarWord(apreciacaoId: string): Promise<Blob> {
    let url = `${API_BASE_URL}/apreciacoes/${apreciacaoId}/exportar-word`;
    // Forçar HTTPS
    if (url.startsWith('http://') && !url.includes('localhost') && !url.includes('127.0.0.1')) {
      url = url.replace('http://', 'https://');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };


    const response = await fetchApi(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({}), // Enviar objeto vazio para evitar erro do Fastify
      mode: 'cors',
      credentials: 'include',
    });

    // Se for 401 (não autorizado), apenas limpar token
    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      const errorMessage = error.message || 'Erro ao exportar Word';
      const errorDetails = error.log || error.stderr || error.error || error.hint || '';
      const fullMessage = errorDetails
        ? `${errorMessage}\n\n${errorDetails}`
        : errorMessage;
      throw new Error(fullMessage);
    }

    return await response.blob();
  }

  /**
   * Exporta a apreciação como Word (.docx) usando Pandoc (LaTeX → Word direto - básico)
   */
  async exportarWordViaPandoc(apreciacaoId: string, latex: string): Promise<Blob> {
    let url = `${API_BASE_URL}/apreciacoes/${apreciacaoId}/exportar-word/pandoc`;
    if (url.startsWith('http://') && !url.includes('localhost') && !url.includes('127.0.0.1')) {
      url = url.replace('http://', 'https://');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };


    const response = await fetchApi(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ latex }),
      mode: 'cors',
      credentials: 'include',
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      const errorMessage = error.message || 'Erro ao exportar Word via Pandoc';
      const errorDetails = error.log || error.stderr || error.error || error.hint || '';
      const fullMessage = errorDetails
        ? `${errorMessage}\n\n${errorDetails}`
        : errorMessage;
      throw new Error(fullMessage);
    }

    return await response.blob();
  }

  /**
   * Exporta a apreciação como Word (.docx) usando Pandoc com opções avançadas (LaTeX → Word direto)
   */
  async exportarWordViaPandocAvancado(apreciacaoId: string, latex: string): Promise<Blob> {
    let url = `${API_BASE_URL}/apreciacoes/${apreciacaoId}/exportar-word/pandoc-advanced`;
    if (url.startsWith('http://') && !url.includes('localhost') && !url.includes('127.0.0.1')) {
      url = url.replace('http://', 'https://');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };


    const response = await fetchApi(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ latex }),
      mode: 'cors',
      credentials: 'include',
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      const errorMessage = error.message || 'Erro ao exportar Word via Pandoc avançado';
      const errorDetails = error.log || error.stderr || error.error || error.hint || '';
      const fullMessage = errorDetails
        ? `${errorMessage}\n\n${errorDetails}`
        : errorMessage;
      throw new Error(fullMessage);
    }

    return await response.blob();
  }

  /**
   * Exporta a apreciação como Word (.docx) usando Python (PDF → Word)
   * Aceita PDF em base64 ou LaTeX para gerar PDF
   */
  async exportarWordViaPython(apreciacaoId: string, pdfBase64?: string, latex?: string): Promise<Blob> {
    let url = `${API_BASE_URL}/apreciacoes/${apreciacaoId}/exportar-word/python`;
    if (url.startsWith('http://') && !url.includes('localhost') && !url.includes('127.0.0.1')) {
      url = url.replace('http://', 'https://');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };


    const bodyPython: { latex?: string; pdfBase64?: string } = {};
    if (pdfBase64) {
      bodyPython.pdfBase64 = pdfBase64;
    } else if (latex) {
      bodyPython.latex = latex;
    }

    const response = await fetchApi(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(bodyPython),
      mode: 'cors',
      credentials: 'include',
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      const errorMessage = error.message || 'Erro ao exportar Word via Python';
      const errorDetails = error.log || error.stderr || error.error || error.hint || '';
      const fullMessage = errorDetails
        ? `${errorMessage}\n\n${errorDetails}`
        : errorMessage;
      throw new Error(fullMessage);
    }

    return await response.blob();
  }

  /**
   * Exporta a apreciação como Word (.docx) usando LibreOffice (PDF → Word)
   * Aceita PDF em base64 ou LaTeX para gerar PDF
   */
  async exportarWordViaLibreOffice(apreciacaoId: string, pdfBase64?: string, latex?: string): Promise<Blob> {
    let url = `${API_BASE_URL}/apreciacoes/${apreciacaoId}/exportar-word/libreoffice`;
    if (url.startsWith('http://') && !url.includes('localhost') && !url.includes('127.0.0.1')) {
      url = url.replace('http://', 'https://');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };


    const body: { latex?: string; pdfBase64?: string } = {};
    if (pdfBase64) {
      body.pdfBase64 = pdfBase64;
    } else if (latex) {
      body.latex = latex;
    }

    const response = await fetchApi(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'include',
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      const errorMessage = error.message || 'Erro ao exportar Word via LibreOffice';
      const errorDetails = error.log || error.stderr || error.error || error.hint || '';
      const fullMessage = errorDetails
        ? `${errorMessage}\n\n${errorDetails}`
        : errorMessage;
      throw new Error(fullMessage);
    }

    return await response.blob();
  }

  /**
   * Exporta a apreciação como Word (.docx) usando pdf2docx avançado (PDF → Word com melhor qualidade)
   * Aceita PDF em base64 ou LaTeX para gerar PDF
   */
  async exportarWordViaPdf2docxAvancado(apreciacaoId: string, pdfBase64?: string, latex?: string): Promise<Blob> {
    let url = `${API_BASE_URL}/apreciacoes/${apreciacaoId}/exportar-word/pdf2docx-advanced`;
    if (url.startsWith('http://') && !url.includes('localhost') && !url.includes('127.0.0.1')) {
      url = url.replace('http://', 'https://');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };


    const body: { latex?: string; pdfBase64?: string } = {};
    if (pdfBase64) {
      body.pdfBase64 = pdfBase64;
    } else if (latex) {
      body.latex = latex;
    }

    const response = await fetchApi(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'include',
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      const errorMessage = error.message || 'Erro ao exportar Word via pdf2docx avançado';
      const errorDetails = error.log || error.stderr || error.error || error.hint || '';
      const fullMessage = errorDetails
        ? `${errorMessage}\n\n${errorDetails}`
        : errorMessage;
      throw new Error(fullMessage);
    }

    return await response.blob();
  }

  /**
   * Exporta a apreciação como Word (.docx) usando Microsoft Word Automation (Windows apenas)
   * Aceita PDF em base64 ou LaTeX para gerar PDF
   */
  async exportarWordViaWordAutomation(apreciacaoId: string, pdfBase64?: string, latex?: string): Promise<Blob> {
    let url = `${API_BASE_URL}/apreciacoes/${apreciacaoId}/exportar-word/word-automation`;
    if (url.startsWith('http://') && !url.includes('localhost') && !url.includes('127.0.0.1')) {
      url = url.replace('http://', 'https://');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };


    const body: { latex?: string; pdfBase64?: string } = {};
    if (pdfBase64) {
      body.pdfBase64 = pdfBase64;
    } else if (latex) {
      body.latex = latex;
    }

    const response = await fetchApi(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'include',
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      const errorMessage = error.message || 'Erro ao exportar Word via Word Automation';
      const errorDetails = error.log || error.stderr || error.error || error.hint || '';
      const fullMessage = errorDetails
        ? `${errorMessage}\n\n${errorDetails}`
        : errorMessage;
      throw new Error(fullMessage);
    }

    return await response.blob();
  }

  /**
   * Exporta a apreciação como Word (.docx) usando OCR + pdf2docx (para PDFs escaneados)
   * Aceita PDF em base64 ou LaTeX para gerar PDF
   */
  async exportarWordViaOCR(apreciacaoId: string, pdfBase64?: string, latex?: string): Promise<Blob> {
    let url = `${API_BASE_URL}/apreciacoes/${apreciacaoId}/exportar-word/ocr`;
    if (url.startsWith('http://') && !url.includes('localhost') && !url.includes('127.0.0.1')) {
      url = url.replace('http://', 'https://');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };


    const body: { latex?: string; pdfBase64?: string } = {};
    if (pdfBase64) {
      body.pdfBase64 = pdfBase64;
    } else if (latex) {
      body.latex = latex;
    }

    const response = await fetchApi(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'include',
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      const errorMessage = error.message || 'Erro ao exportar Word via OCR';
      const errorDetails = error.log || error.stderr || error.error || error.hint || '';
      const fullMessage = errorDetails
        ? `${errorMessage}\n\n${errorDetails}`
        : errorMessage;
      throw new Error(fullMessage);
    }

    return await response.blob();
  }

  /**
   * Exporta a apreciação como Word (.docx) via backend → API externa de conversão PDF → Word
   * Envia o PDF como multipart/form-data para o backend (evita inflação base64)
   *
   * Devolve também o `filename` que o conversor NR12 escolheu (ex.:
   * B0712GAP0351_0001_00.docx). Ele nomeia pela capa que reconstruiu, então é
   * o nome que o usuário deve receber no download.
   */
  async exportarWordViaILovePdf(
    _apreciacaoId: string,
    pdfBlob: Blob,
    capa?: { nome?: string; area?: string; tag?: string }
  ): Promise<{ blob: Blob; filename?: string }> {
    const url = `${API_BASE_URL}/pdf/converter-para-word/upload`;

    const formData = new FormData();
    // Contrato do /nr12: campos antes do arquivo. `nome` é o equipamento (vira o
    // TÍTULO da capa), `area` é o setor e `tag` o nº de identificação da máquina.
    // Sem `nome` o conversor cai no nome do arquivo temporário.
    const campos = {
      nome: capa?.nome?.trim(),
      area: capa?.area?.trim(),
      tag: capa?.tag?.trim(),
    };
    for (const [chave, valor] of Object.entries(campos)) {
      if (valor) {
        formData.append(chave, valor);
      }
    }
    formData.append('pdf', pdfBlob, 'documento.pdf');

    const headers: Record<string, string> = {};


    const response = await fetchApi(url, {
      method: 'POST',
      headers,
      body: formData,
      mode: 'cors',
      credentials: 'include',
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      throw new Error(error.message || 'Erro ao exportar Word');
    }

    return {
      blob: await response.blob(),
      filename: extrairNomeArquivoContentDisposition(response.headers.get('content-disposition')),
    };
  }

  // Ativos
  async listarAtivos(params?: {
    page?: number;
    limit?: number;
    area?: string;
    local?: string;
    status?: string;
    tipo?: string;
    busca?: string;
    numeroPraxis?: string;
    numeroAmt?: string;
    refSismana?: string;
    comApreciacao?: boolean;
    comDocumento?: boolean;
    projetoId?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.area) queryParams.append('area', params.area);
    if (params?.local) queryParams.append('local', params.local);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.tipo) queryParams.append('tipo', params.tipo);
    if (params?.busca) queryParams.append('busca', params.busca);
    if (params?.numeroPraxis) queryParams.append('numeroPraxis', params.numeroPraxis);
    if (params?.numeroAmt) queryParams.append('numeroAmt', params.numeroAmt);
    if (params?.refSismana) queryParams.append('refSismana', params.refSismana);
    if (params?.comApreciacao !== undefined) queryParams.append('comApreciacao', params.comApreciacao.toString());
    if (params?.comDocumento !== undefined) queryParams.append('comDocumento', params.comDocumento.toString());
    if (params?.projetoId) queryParams.append('projetoId', params.projetoId);

    const query = queryParams.toString();
    return this.request(`/ativos${query ? `?${query}` : ''}`);
  }

  async buscarAtivo(id: string): Promise<ApiResponse> {
    return this.request(`/ativos/${id}`);
  }

  async importarAtivos(filePath: string, projetoId?: string): Promise<ApiResponse> {
    return this.request('/ativos/importar', {
      method: 'POST',
      body: JSON.stringify({ filePath, projetoId }),
    });
  }

  async criarAtivo(dados: any): Promise<ApiResponse> {
    return this.request('/ativos', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarAtivo(id: string, dados: any): Promise<ApiResponse> {
    return this.request(`/ativos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerAtivo(id: string): Promise<ApiResponse> {
    return this.request(`/ativos/${id}`, {
      method: 'DELETE',
    });
  }

  async obterEstatisticasAtivos(projetoId?: string): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (projetoId) queryParams.append('projetoId', projetoId);
    const query = queryParams.toString();
    return this.request(`/ativos/estatisticas${query ? `?${query}` : ''}`);
  }

  async obterRelatorioMensal(params: {
    projetoId: string;
    ano: number;
    mes: number;
    anoFim?: number;
    mesFim?: number;
  }): Promise<ApiResponse<RelatorioMensalResponse>> {
    const queryParams = new URLSearchParams();
    queryParams.append('projetoId', params.projetoId);
    queryParams.append('ano', String(params.ano));
    queryParams.append('mes', String(params.mes));
    if (params.anoFim !== undefined && params.mesFim !== undefined) {
      queryParams.append('anoFim', String(params.anoFim));
      queryParams.append('mesFim', String(params.mesFim));
    }
    return this.request(`/relatorios/mensal?${queryParams.toString()}`);
  }

  async exportarRelatorioMensalPdf(params: {
    projetoId: string;
    ano: number;
    mes: number;
    anoFim?: number;
    mesFim?: number;
    somenteNaoConformes?: boolean;
    /** Idioma do PDF gerado no backend. Sem valor, o documento sai em português. */
    idioma?: 'pt' | 'en';
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('projetoId', params.projetoId);
    queryParams.append('ano', String(params.ano));
    queryParams.append('mes', String(params.mes));
    if (params.anoFim !== undefined && params.mesFim !== undefined) {
      queryParams.append('anoFim', String(params.anoFim));
      queryParams.append('mesFim', String(params.mesFim));
    }
    if (params.somenteNaoConformes) {
      queryParams.append('somenteNaoConformes', '1');
    }
    if (params.idioma) {
      queryParams.append('idioma', params.idioma);
    }
    const url = `${API_BASE_URL}/relatorios/mensal/pdf?${queryParams.toString()}`;

    const headers: Record<string, string> = {};

    const response = await fetchApi(url, {
      method: 'GET',
      mode: 'cors',
      headers,
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      throw new Error(error.message || 'Erro ao exportar PDF do relatório mensal.');
    }

    return await response.blob();
  }

  async exportarRelatorioOnePagePdf(params: {
    projetoId: string;
    ano: number;
    mes: number;
    anoFim?: number;
    mesFim?: number;
    cronogramaAntigo?: boolean;
    /** Idioma do PDF gerado no backend. Sem valor, o documento sai em português. */
    idioma?: 'pt' | 'en';
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('projetoId', params.projetoId);
    queryParams.append('ano', String(params.ano));
    queryParams.append('mes', String(params.mes));
    if (params.anoFim !== undefined && params.mesFim !== undefined) {
      queryParams.append('anoFim', String(params.anoFim));
      queryParams.append('mesFim', String(params.mesFim));
    }
    if (params.cronogramaAntigo) {
      queryParams.append('cronogramaAntigo', '1');
    }
    if (params.idioma) {
      queryParams.append('idioma', params.idioma);
    }
    const url = `${API_BASE_URL}/relatorios/mensal/one-page-pdf?${queryParams.toString()}`;

    const headers: Record<string, string> = {};

    const response = await fetchApi(url, {
      method: 'GET',
      mode: 'cors',
      headers,
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      throw new Error(error.message || 'Erro ao exportar PDF do relatório one page.');
    }

    return await response.blob();
  }

  async exportarCronogramaExcel(params: {
    projetoId: string;
    ano: number;
    mes: number;
    anoFim?: number;
    mesFim?: number;
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('projetoId', params.projetoId);
    queryParams.append('ano', String(params.ano));
    queryParams.append('mes', String(params.mes));
    if (params.anoFim !== undefined && params.mesFim !== undefined) {
      queryParams.append('anoFim', String(params.anoFim));
      queryParams.append('mesFim', String(params.mesFim));
    }
    const url = `${API_BASE_URL}/relatorios/mensal/cronograma-excel?${queryParams.toString()}`;

    const headers: Record<string, string> = {};

    const response = await fetchApi(url, { method: 'GET', mode: 'cors', headers });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      throw new Error(error.message || 'Erro ao exportar cronograma em Excel.');
    }

    return await response.blob();
  }

  async exportarRelatorioOnePageSemanalPdf(params: {
    projetoId: string;
    data: string;
    /** Idioma do PDF gerado no backend. Sem valor, o documento sai em português. */
    idioma?: 'pt' | 'en';
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('projetoId', params.projetoId);
    queryParams.append('data', params.data);
    if (params.idioma) {
      queryParams.append('idioma', params.idioma);
    }
    const url = `${API_BASE_URL}/relatorios/semanal/one-page-pdf?${queryParams.toString()}`;

    const headers: Record<string, string> = {};

    const response = await fetchApi(url, {
      method: 'GET',
      mode: 'cors',
      headers,
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      throw new Error(error.message || 'Erro ao exportar PDF do relatório one page semanal.');
    }

    return await response.blob();
  }

  async exportarRelatorioOnePageTresSemanasPdf(params: {
    projetoId: string;
    data: string;
    proximaSemana?: Array<{ titulo: string; descricao: string; categoria?: 'planejado' | 'atrasado' | 'pendencia' }>;
    /** Idioma do PDF gerado no backend. Sem valor, o documento sai em português. */
    idioma?: 'pt' | 'en';
  }): Promise<Blob> {
    const url = `${API_BASE_URL}/relatorios/tres-semanas/one-page-pdf`;

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    const response = await fetchApi(url, {
      method: 'POST',
      mode: 'cors',
      headers,
      body: JSON.stringify({
        projetoId: params.projetoId,
        data: params.data,
        proximaSemana: params.proximaSemana ?? [],
        ...(params.idioma ? { idioma: params.idioma } : {}),
      }),
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      throw new Error(error.message || 'Erro ao exportar PDF do relatório one page de 3 semanas.');
    }

    return await response.blob();
  }

  async gerarGuiaRemessa(params: {
    projetoId: string;
    destinatario: string;
    observacoes?: string | null;
    apreciacaoIds: string[];
  }): Promise<{ blob: Blob; numero: string | null }> {
    return this.baixarPdfGuiaRemessa(`${API_BASE_URL}/guias-remessa`, params);
  }

  async listarGuiasRemessa(projetoId: string): Promise<ApiResponse<Array<{
    id: string;
    numero: string;
    destinatario: string;
    observacoes: string | null;
    criadoEm: string;
    totalItens: number;
    emitente: string | null;
  }>>> {
    return this.request(`/guias-remessa?projetoId=${encodeURIComponent(projetoId)}`);
  }

  async removerApreciacaoGuiaRemessa(guiaId: string, apreciacaoId: string): Promise<ApiResponse<{ totalItens: number }>> {
    return this.request(
      `/guias-remessa/${encodeURIComponent(guiaId)}/apreciacoes/${encodeURIComponent(apreciacaoId)}`,
      { method: 'DELETE' },
    );
  }

  async adicionarApreciacoesGuiaRemessa(params: {
    guiaId: string;
    apreciacaoIds: string[];
  }): Promise<{ blob: Blob; numero: string | null }> {
    return this.baixarPdfGuiaRemessa(
      `${API_BASE_URL}/guias-remessa/${encodeURIComponent(params.guiaId)}/apreciacoes`,
      { apreciacaoIds: params.apreciacaoIds },
    );
  }

  private async baixarPdfGuiaRemessa(url: string, body: unknown): Promise<{ blob: Blob; numero: string | null }> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    const response = await fetchApi(url, {
      method: 'POST',
      mode: 'cors',
      headers,
      body: JSON.stringify(body),
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      throw new Error(error.message || 'Erro ao gerar guia de remessa.');
    }

    const numero = response.headers.get('X-Guia-Numero');
    const blob = await response.blob();
    return { blob, numero };
  }

  async importarCronogramaBase(params: {
    projetoId: string;
    filePath?: string;
  }): Promise<ApiResponse<{
    cronogramaProjetoId: string;
    projetoId: string;
    arquivoOrigem: string;
    totalTarefas: number;
    periodoInicioIso: string | null;
    periodoFimIso: string | null;
  }>> {
    return this.request('/cronograma/importar-base', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async listarCronograma(params: {
    projetoId: string;
    ano?: number;
    mes?: number;
    nivel?: number;
    semana?: string;
    area?: string;
  }): Promise<ApiResponse<CronogramaListResponse>> {
    const queryParams = new URLSearchParams();
    queryParams.append('projetoId', params.projetoId);
    if (params.ano) queryParams.append('ano', String(params.ano));
    if (params.mes) queryParams.append('mes', String(params.mes));
    if (params.nivel) queryParams.append('nivel', String(params.nivel));
    if (params.semana) queryParams.append('semana', params.semana);
    if (params.area) queryParams.append('area', params.area);
    return this.request(`/cronograma?${queryParams.toString()}`);
  }

  async atualizarCronogramaTarefa(params: {
    tarefaId: string;
    projetoId: string;
    payload: Partial<{ [K in keyof Pick<CronogramaTaskResponse,
      'nomeTarefa' | 'sismanaAntigo' | 'area' | 'local' | 'semana' | 'mes'
      | 'duracaoRaw' | 'duracaoDias' | 'predecessoras' | 'inicioPlanejado'
      | 'terminoPlanejado' | 'percentualOverride' | 'ativoId'
    >]: CronogramaTaskResponse[K] | null }>;
  }): Promise<ApiResponse<CronogramaTaskResponse>> {
    return this.request(`/cronograma/tarefas/${params.tarefaId}`, {
      method: 'PUT',
      body: JSON.stringify({
        projetoId: params.projetoId,
        ...params.payload,
      }),
    });
  }

  async sincronizarAtivosCronograma(projetoId: string): Promise<ApiResponse<{ ativos_criados: number; tarefas_vinculadas: number }>> {
    return this.request(`/cronograma/${projetoId}/sincronizar-ativos`, { method: 'POST', body: JSON.stringify({}) });
  }

  async listarRevisoesCronograma(params: {
    projetoId: string;
    tarefaId?: string;
  }): Promise<ApiResponse<CronogramaRevisaoResponse[]>> {
    const queryParams = new URLSearchParams();
    queryParams.append('projetoId', params.projetoId);
    if (params.tarefaId) queryParams.append('tarefaId', params.tarefaId);
    return this.request(`/cronograma/revisoes?${queryParams.toString()}`);
  }

  async obterValoresUnicosAtivos(): Promise<ApiResponse> {
    return this.request('/ativos/valores-unicos');
  }

  // Templates
  async listarTemplates(params?: {
    page?: number;
    limit?: number;
    tipo?: string;
    norma?: string;
    ativo?: boolean;
    busca?: string;
    projetoId?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.tipo) queryParams.append('tipo', params.tipo);
    if (params?.norma) queryParams.append('norma', params.norma);
    if (params?.ativo !== undefined) queryParams.append('ativo', params.ativo.toString());
    if (params?.busca) queryParams.append('busca', params.busca);
    if (params?.projetoId) queryParams.append('projetoId', params.projetoId);

    const query = queryParams.toString();
    return this.request(`/templates${query ? `?${query}` : ''}`);
  }

  async buscarTemplate(id: string): Promise<ApiResponse> {
    return this.request(`/templates/${id}`);
  }

  /** Pictogramas e plaquetas de segurança padrão (sinalização reaproveitável). */
  async listarPictogramas(): Promise<ApiResponse<UploadedFoto[]>> {
    return this.request('/pictogramas');
  }

  async criarTemplate(dados: any): Promise<ApiResponse> {
    return this.request('/templates', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async criarTemplateDeApreciacao(apreciacaoId: string, dados: { nome: string; descricao?: string }): Promise<ApiResponse> {
    return this.request(`/templates/de-apreciacao/${apreciacaoId}`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarTemplate(id: string, dados: any): Promise<ApiResponse> {
    return this.request(`/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerTemplate(id: string): Promise<ApiResponse> {
    return this.request(`/templates/${id}`, {
      method: 'DELETE',
    });
  }

  async duplicarTemplate(id: string, novoNome: string): Promise<ApiResponse> {
    return this.request(`/templates/${id}/duplicar`, {
      method: 'POST',
      body: JSON.stringify({ nome: novoNome }),
    });
  }

  async aplicarTemplate(
    templateId: string,
    apreciacaoId: string,
    partesSelecionadas?: string[]
  ): Promise<ApiResponse> {
    return this.request(`/templates/${templateId}/aplicar/${apreciacaoId}`, {
      method: 'POST',
      body: partesSelecionadas && partesSelecionadas.length > 0
        ? JSON.stringify({ partesSelecionadas })
        : undefined,
    });
  }

  // Métodos para Templates de Parte
  async listarTemplatePartes(projetoId?: string, busca?: string, ativo?: boolean): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (projetoId) params.append('projetoId', projetoId);
    if (busca) params.append('busca', busca);
    if (ativo !== undefined) params.append('ativo', ativo.toString());
    const query = params.toString();
    return this.request(`/template-partes${query ? `?${query}` : ''}`, {
      method: 'GET',
    });
  }

  async buscarTemplateParte(id: string): Promise<ApiResponse> {
    return this.request(`/template-partes/${id}`, {
      method: 'GET',
    });
  }

  async criarTemplateParte(dados: any): Promise<ApiResponse> {
    return this.request('/template-partes', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async criarTemplateParteDeApreciacao(
    apreciacaoId: string,
    parteNome: string,
    dados: { nome: string; descricao?: string }
  ): Promise<ApiResponse> {
    return this.request('/template-partes/de-apreciacao', {
      method: 'POST',
      body: JSON.stringify({
        apreciacaoId,
        parteNome,
        ...dados,
      }),
    });
  }

  async aplicarTemplateParte(templateParteId: string, apreciacaoId: string): Promise<ApiResponse> {
    return this.request(`/template-partes/${templateParteId}/aplicar/${apreciacaoId}`, {
      method: 'POST',
    });
  }

  async atualizarTemplateParte(id: string, dados: any): Promise<ApiResponse> {
    return this.request(`/template-partes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerTemplateParte(id: string): Promise<ApiResponse> {
    return this.request(`/template-partes/${id}`, {
      method: 'DELETE',
    });
  }

  // Métodos para Grupos de Templates
  async listarGrupos(params?: {
    page?: number;
    limit?: number;
    projetoId?: string;
    ativo?: boolean;
    busca?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.projetoId) queryParams.append('projetoId', params.projetoId);
    if (params?.ativo !== undefined) queryParams.append('ativo', params.ativo.toString());
    if (params?.busca) queryParams.append('busca', params.busca);

    const query = queryParams.toString();
    return this.request(`/template-grupos${query ? `?${query}` : ''}`);
  }

  async buscarGrupo(id: string): Promise<ApiResponse> {
    return this.request(`/template-grupos/${id}`);
  }

  async criarGrupo(dados: { nome: string; descricao?: string; projetoId: string; ativo?: boolean }): Promise<ApiResponse> {
    return this.request('/template-grupos', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarGrupo(id: string, dados: { nome?: string; descricao?: string; ativo?: boolean }): Promise<ApiResponse> {
    return this.request(`/template-grupos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerGrupo(id: string): Promise<ApiResponse> {
    return this.request(`/template-grupos/${id}`, {
      method: 'DELETE',
    });
  }

  // Métodos para Templates Multipartes
  async listarTemplateMultiPartes(params?: {
    page?: number;
    limit?: number;
    projetoId?: string;
    grupoId?: string;
    tipoMaquina?: string;
    ativo?: boolean;
    busca?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.projetoId) queryParams.append('projetoId', params.projetoId);
    if (params?.grupoId) queryParams.append('grupoId', params.grupoId);
    if (params?.tipoMaquina) queryParams.append('tipoMaquina', params.tipoMaquina);
    if (params?.ativo !== undefined) queryParams.append('ativo', params.ativo.toString());
    if (params?.busca) queryParams.append('busca', params.busca);

    const query = queryParams.toString();
    return this.request(`/template-multipartes${query ? `?${query}` : ''}`);
  }

  async buscarTemplateMultiParte(id: string): Promise<ApiResponse> {
    return this.request(`/template-multipartes/${id}`);
  }

  async criarTemplateMultiParte(dados: {
    nome: string;
    descricao?: string;
    tipoMaquina?: string;
    grupoId?: string;
    projetoId: string;
    partes: Array<{ nome: string; riscos: any[] }>;
    ativo?: boolean;
  }): Promise<ApiResponse> {
    return this.request('/template-multipartes', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async criarTemplateMultiParteDeApreciacao(
    apreciacaoId: string,
    dados: { nome: string; descricao?: string; tipoMaquina?: string; grupoId?: string }
  ): Promise<ApiResponse> {
    return this.request(`/template-multipartes/de-apreciacao/${apreciacaoId}`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async aplicarTemplateMultiParte(
    templateId: string,
    apreciacaoId: string,
    partesSelecionadas?: string[]
  ): Promise<ApiResponse> {
    return this.request(`/template-multipartes/${templateId}/aplicar/${apreciacaoId}`, {
      method: 'POST',
      body: partesSelecionadas && partesSelecionadas.length > 0
        ? JSON.stringify({ partesSelecionadas })
        : undefined,
    });
  }

  async atualizarTemplateMultiParte(id: string, dados: {
    nome?: string;
    descricao?: string | null;
    tipoMaquina?: string | null;
    grupoId?: string | null;
    partes?: Array<{ nome: string; riscos: any[] }>;
    ativo?: boolean;
  }): Promise<ApiResponse> {
    return this.request(`/template-multipartes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerTemplateMultiParte(id: string): Promise<ApiResponse> {
    return this.request(`/template-multipartes/${id}`, {
      method: 'DELETE',
    });
  }

  // Locks
  async criarLock(dados: {
    tipoRecurso: 'apreciacao' | 'maquina' | 'ativo';
    recursoId: string;
    duracaoMinutos?: number;
  }): Promise<ApiResponse> {
    return this.request('/locks', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async verificarLock(tipoRecurso: 'apreciacao' | 'maquina' | 'ativo', recursoId: string): Promise<ApiResponse> {
    return this.request(`/locks/${tipoRecurso}/${recursoId}`);
  }

  async removerLock(
    tipoRecurso: 'apreciacao' | 'maquina' | 'ativo',
    recursoId: string
  ): Promise<ApiResponse> {
    return this.request(`/locks/${tipoRecurso}/${recursoId}`, {
      method: 'DELETE',
    });
  }

  async renovarLock(
    tipoRecurso: 'apreciacao' | 'maquina' | 'ativo',
    recursoId: string,
    duracaoMinutos?: number
  ): Promise<ApiResponse> {
    return this.request(`/locks/${tipoRecurso}/${recursoId}/renovar`, {
      method: 'POST',
      body: JSON.stringify({ duracaoMinutos }),
    });
  }

  async listarLocksUsuario(usuarioId: string): Promise<ApiResponse> {
    return this.request(`/locks/usuario/${usuarioId}`);
  }

  // Máquinas
  async listarMaquinas(params?: {
    projetoId?: string;
    area?: string;
    tipo?: string;
    busca?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.projetoId) queryParams.append('projetoId', params.projetoId);
    if (params?.area) queryParams.append('area', params.area);
    if (params?.tipo) queryParams.append('tipo', params.tipo);
    if (params?.busca) queryParams.append('busca', params.busca);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return this.request(`/maquinas${query ? `?${query}` : ''}`);
  }

  async buscarMaquina(id: string): Promise<ApiResponse> {
    return this.request(`/maquinas/${id}`);
  }

  async criarMaquina(dados: any): Promise<ApiResponse> {
    return this.request('/maquinas', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarMaquina(id: string, dados: any): Promise<ApiResponse> {
    return this.request(`/maquinas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async deletarMaquina(id: string): Promise<ApiResponse> {
    return this.request(`/maquinas/${id}`, {
      method: 'DELETE',
    });
  }

  // Autenticação
  //
  // O cabeçalho X-Sessao: cookie diz ao backend que este cliente guarda a
  // sessão em cookie HttpOnly. Com ele, o token NÃO volta no corpo da resposta
  // — nem no login, nem na renovação —, então nem um XSS consegue lê-lo.
  async login(email: string, senha: string): Promise<ApiResponse<{ user: any; expiraEm: number }>> {
    return this.request('/auth/login', {
      method: 'POST',
      headers: { 'X-Sessao': 'cookie' },
      body: JSON.stringify({ email, senha }),
    });
  }

  /** Confere se o cookie de sessão ainda vale. */
  async verificarSessao(): Promise<ApiResponse> {
    return this.request('/auth/verify', { method: 'POST' });
  }

  /**
   * Troca o refresh token (cookie) por uma sessão nova.
   * Chamadas simultâneas compartilham a mesma renovação.
   */
  async renovarSessao(): Promise<boolean> {
    if (this.renovacaoEmCurso) return this.renovacaoEmCurso;

    this.renovacaoEmCurso = (async () => {
      const tentar = async (): Promise<Response | null> => {
        try {
          return await fetchApi(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            mode: 'cors',
            headers: { 'X-Sessao': 'cookie' },
          });
        } catch {
          return null;
        }
      };

      const resposta = await tentar();
      if (!resposta) return false;
      if (resposta.ok) return true;

      // O refresh token é rotativo: se outra aba renovou primeiro, esta chega
      // com o token anterior. O cookie novo já está no navegador — repetir
      // resolve, e a sessão não cai.
      const corpo = await resposta.json().catch(() => null);
      if (corpo?.errors?.includes('RENOVACAO_CONCORRENTE')) {
        await new Promise((resolver) => setTimeout(resolver, 400));
        const segunda = await tentar();
        return Boolean(segunda?.ok);
      }

      return false;
    })();

    try {
      return await this.renovacaoEmCurso;
    } finally {
      this.renovacaoEmCurso = null;
    }
  }

  /** Encerra a sessão no servidor (revoga o refresh token e limpa os cookies). */
  async logoutSessao(): Promise<void> {
    try {
      await fetchApi(`${API_BASE_URL}/auth/logout`, { method: 'POST', mode: 'cors' });
    } catch {
      // Sair localmente não pode depender do servidor responder.
    }
  }

  /** Encerra a sessão em todos os dispositivos (invalida tokens já emitidos). */
  async logoutTodosDispositivos(): Promise<ApiResponse> {
    return this.request('/auth/logout-all', { method: 'POST' });
  }

  async getMe(): Promise<ApiResponse> {
    return this.request('/auth/me');
  }

  // Projetos
  async listarProjetos(params?: {
    page?: number;
    limit?: number;
    ativo?: boolean;
    busca?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.ativo !== undefined) queryParams.append('ativo', params.ativo.toString());
    if (params?.busca) queryParams.append('busca', params.busca);

    const query = queryParams.toString();
    return this.request(`/projetos${query ? `?${query}` : ''}`);
  }

  async buscarProjeto(id: string): Promise<ApiResponse> {
    return this.request(`/projetos/${id}`);
  }

  async criarProjeto(dados: { nome: string; descricao?: string; clienteTipo?: string | null; configuracaoDocumento?: any; squadId?: string | null; tipoProjeto?: string; budgetHabilitado?: boolean; budgetTotal?: number | null }): Promise<ApiResponse> {
    return this.request('/projetos', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarProjeto(id: string, dados: { nome?: string; descricao?: string; ativo?: boolean; clienteTipo?: string | null; configuracaoDocumento?: any; squadId?: string | null; tipoProjeto?: string; budgetHabilitado?: boolean; budgetTotal?: number | null }): Promise<ApiResponse> {
    return this.request(`/projetos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  // Budget do projeto (obras → máquinas → gastos)
  async buscarBudget(projetoId: string): Promise<ApiResponse> {
    return this.request(`/projetos/${projetoId}/budget`);
  }

  async criarObraBudget(projetoId: string, dados: { nome: string; descricao?: string }): Promise<ApiResponse> {
    return this.request(`/projetos/${projetoId}/budget/obras`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarObraBudget(id: string, dados: { nome?: string; descricao?: string }): Promise<ApiResponse> {
    return this.request(`/budget/obras/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerObraBudget(id: string): Promise<ApiResponse> {
    return this.request(`/budget/obras/${id}`, {
      method: 'DELETE',
    });
  }

  async criarMaquinaBudget(obraId: string, dados: { nome: string; budget?: number; rubricaId?: string | null }): Promise<ApiResponse> {
    return this.request(`/budget/obras/${obraId}/maquinas`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  // Rubricas: as frentes de custo que viram grupos de colunas na planilha
  async criarRubricaBudget(obraId: string, dados: { nome: string; vinculada?: boolean }): Promise<ApiResponse> {
    return this.request(`/budget/obras/${obraId}/rubricas`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarRubricaBudget(id: string, dados: { nome?: string; ordem?: number; vinculada?: boolean }): Promise<ApiResponse> {
    return this.request(`/budget/rubricas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerRubricaBudget(id: string, manterLinhas = false): Promise<ApiResponse> {
    return this.request(`/budget/rubricas/${id}?manterLinhas=${manterLinhas}`, {
      method: 'DELETE',
    });
  }

  /** Junta abas: cada obra de origem vira uma rubrica dentro da obra destino */
  async juntarObrasBudget(destinoId: string, dados: {
    origens: Array<{ obraId: string; rubrica: string }>;
    rubricaDestino?: string | null;
    mapaNomes?: Record<string, string>;
  }): Promise<ApiResponse> {
    return this.request(`/budget/obras/${destinoId}/juntar`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async renomearMaquinaBudget(obraId: string, dados: { de: string; para: string; rubricaId?: string | null }): Promise<ApiResponse> {
    return this.request(`/budget/obras/${obraId}/renomear-maquina`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async atualizarMaquinaBudget(id: string, dados: { nome?: string; budget?: number }): Promise<ApiResponse> {
    return this.request(`/budget/maquinas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  // Exclusão em lote: uma requisição, um ponto no histórico, uma transação
  async removerMaquinasBudget(obraId: string, ids: string[]): Promise<ApiResponse> {
    return this.request(`/budget/obras/${obraId}/remover-maquinas`, {
      method: 'POST',
      body: JSON.stringify({ ids })
    });
  }

  // Histórico de estado da planilha (o "desfazer" que sobrevive a recarregar)
  async historicoBudget(obraId: string): Promise<ApiResponse> {
    return this.request(`/budget/obras/${obraId}/historico`);
  }

  async restaurarPontoBudget(pontoId: string): Promise<ApiResponse> {
    return this.request(`/budget/historico/${pontoId}/restaurar`, { method: 'POST' });
  }

  async removerMaquinaBudget(id: string): Promise<ApiResponse> {
    return this.request(`/budget/maquinas/${id}`, {
      method: 'DELETE',
    });
  }

  async lancarGastosLoteBudget(obraId: string, dados: { valor: number; modo?: 'por_maquina' | 'rateio' | 'proporcional'; etapa?: string; tipo?: string; data?: string; maquinaIds?: string[]; dividirMetade?: boolean; apenasSemValor?: boolean }): Promise<ApiResponse> {
    return this.request(`/budget/obras/${obraId}/gastos-lote`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async renomearEtapaBudget(obraId: string, dados: { de?: string | null; para: string }): Promise<ApiResponse> {
    return this.request(`/budget/obras/${obraId}/renomear-etapa`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerEtapaBudget(obraId: string, etapa?: string | null): Promise<ApiResponse> {
    return this.request(`/budget/obras/${obraId}/remover-etapa`, {
      method: 'PUT',
      body: JSON.stringify({ etapa: etapa ?? null }),
    });
  }

  async definirGastoEtapaBudget(maquinaId: string, dados: { etapa?: string; tipo?: string; valor: number }): Promise<ApiResponse> {
    return this.request(`/budget/maquinas/${maquinaId}/gasto-etapa`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async criarGastoBudget(maquinaId: string, dados: { valor: number; etapa?: string; descricao?: string; tipo?: string; data?: string }): Promise<ApiResponse> {
    return this.request(`/budget/maquinas/${maquinaId}/gastos`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarGastoBudget(id: string, dados: { valor?: number; etapa?: string; descricao?: string; tipo?: string; data?: string }): Promise<ApiResponse> {
    return this.request(`/budget/gastos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerGastoBudget(id: string): Promise<ApiResponse> {
    return this.request(`/budget/gastos/${id}`, {
      method: 'DELETE',
    });
  }

  // ===================== Carteira de projetos (PIs) =====================

  async listarImportacoesCarteira(): Promise<ApiResponse> {
    return this.request('/carteira/importacoes');
  }

  async obterResumoCarteira(filtros?: FiltrosCarteira): Promise<ApiResponse> {
    return this.request(`/carteira/resumo${queryCarteira(filtros)}`);
  }

  async listarPisCarteira(
    filtros?: FiltrosCarteira & { status?: string; ordenar?: string; limite?: number }
  ): Promise<ApiResponse> {
    return this.request(`/carteira/pis${queryCarteira(filtros)}`);
  }

  async detalharPiCarteira(id: string): Promise<ApiResponse> {
    return this.request(`/carteira/pis/${id}`);
  }

  async importarCarteira(
    arquivo: File,
    dados?: { referencia?: string; anoBase?: number; observacao?: string }
  ): Promise<ApiResponse> {
    const form = new FormData();
    // O arquivo vai por último: o backend lê os campos de texto de `fields`,
    // que só estão populados quando já passaram pelo parser do multipart.
    if (dados?.referencia) form.append('referencia', dados.referencia);
    if (dados?.anoBase) form.append('anoBase', String(dados.anoBase));
    if (dados?.observacao) form.append('observacao', dados.observacao);
    form.append('arquivo', arquivo, arquivo.name);

    const headers: Record<string, string> = {};

    const response = await fetchApi(`${API_BASE_URL}/carteira/importar`, {
      method: 'POST',
      headers,
      body: form,
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    return await response.json();
  }

  async removerImportacaoCarteira(id: string): Promise<ApiResponse> {
    return this.request(`/carteira/importacoes/${id}`, { method: 'DELETE' });
  }

  // ============ Carteira → custo do time (tudo restrito a admin) ============

  async obterGradeCustoCarteira(competencia?: string, incluirInativos = false): Promise<ApiResponse> {
    return this.request(`/carteira/custo/grade${queryCarteira({ competencia, incluirInativos })}`);
  }

  async listarCompetenciasCarteira(): Promise<ApiResponse> {
    return this.request('/carteira/custo/competencias');
  }

  async obterCustoAcumuladoCarteira(ate?: string): Promise<ApiResponse> {
    return this.request(`/carteira/custo/acumulado${queryCarteira({ ate })}`);
  }

  async definirAlocacaoCarteira(dados: {
    competencia: string;
    userId: string;
    piCodigo: string;
    percentual: number;
    observacao?: string | null;
  }): Promise<ApiResponse> {
    return this.request('/carteira/custo/alocacao', { method: 'PUT', body: JSON.stringify(dados) });
  }

  async copiarAlocacaoCarteira(dados: { de: string; para: string; substituir?: boolean }): Promise<ApiResponse> {
    return this.request('/carteira/custo/alocacao/copiar', { method: 'POST', body: JSON.stringify(dados) });
  }

  async definirCustoPessoaCarteira(
    userId: string,
    dados: { vigenciaInicio: string; custoMensal: number; observacao?: string | null }
  ): Promise<ApiResponse> {
    return this.request(`/carteira/custo/pessoas/${userId}`, { method: 'PUT', body: JSON.stringify(dados) });
  }

  async obterHistoricoCustoPessoaCarteira(userId: string): Promise<ApiResponse> {
    return this.request(`/carteira/custo/pessoas/${userId}`);
  }

  async removerVigenciaCustoCarteira(id: string): Promise<ApiResponse> {
    return this.request(`/carteira/custo/vigencias/${id}`, { method: 'DELETE' });
  }

  async definirKraCarteira(dados: { competencia: string; valor: number; observacao?: string | null }): Promise<ApiResponse> {
    return this.request('/carteira/custo/kra', { method: 'PUT', body: JSON.stringify(dados) });
  }

  async listarPoolsCarteira(): Promise<ApiResponse> {
    return this.request('/carteira/custo/pools');
  }

  async salvarPoolCarteira(dados: {
    id?: string;
    nome: string;
    valor: number;
    competenciaInicio: string;
    competenciaFim: string;
    criterio: string;
    piCodigos?: string[];
    observacao?: string | null;
  }): Promise<ApiResponse> {
    return this.request('/carteira/custo/pools', { method: 'PUT', body: JSON.stringify(dados) });
  }

  async removerPoolCarteira(id: string): Promise<ApiResponse> {
    return this.request(`/carteira/custo/pools/${id}`, { method: 'DELETE' });
  }

  async listarKraCarteira(): Promise<ApiResponse> {
    return this.request('/carteira/custo/kra');
  }

  // ===================== Manuais de Operação =====================

  /** Histórico de manuais do projeto (sem o corpo dos documentos). */
  async listarManuais(filtros?: {
    projetoId?: string;
    busca?: string;
    status?: string;
    incluirInativos?: boolean;
    pagina?: number;
    limite?: number;
  }): Promise<ApiResponse> {
    const query = new URLSearchParams();
    if (filtros?.projetoId) query.set('projetoId', filtros.projetoId);
    if (filtros?.busca) query.set('busca', filtros.busca);
    if (filtros?.status) query.set('status', filtros.status);
    if (filtros?.incluirInativos) query.set('incluirInativos', 'true');
    if (filtros?.pagina) query.set('pagina', String(filtros.pagina));
    if (filtros?.limite) query.set('limite', String(filtros.limite));
    const sufixo = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/manuais${sufixo}`);
  }

  async estatisticasManuais(projetoId?: string): Promise<ApiResponse> {
    const sufixo = projetoId ? `?projetoId=${encodeURIComponent(projetoId)}` : '';
    return this.request(`/manuais/estatisticas${sufixo}`);
  }

  /** Documento completo, com as últimas versões congeladas. */
  async buscarManual(id: string): Promise<ApiResponse> {
    return this.request(`/manuais/${id}`);
  }

  async criarManual(dados: {
    projetoId: string;
    dados: unknown;
    titulo?: string;
    status?: string;
    metricas?: { progresso?: number; pendenciasCriticas?: number; totalPaginas?: number };
  }): Promise<ApiResponse> {
    return this.request('/manuais', { method: 'POST', body: JSON.stringify(dados) });
  }

  /** Gravação do editor. Sem `dados`, altera só os metadados. */
  async atualizarManual(id: string, dados: {
    dados?: unknown;
    titulo?: string;
    status?: string;
    metricas?: { progresso?: number; pendenciasCriticas?: number; totalPaginas?: number };
  }): Promise<ApiResponse> {
    return this.request(`/manuais/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
  }

  /** "Criar igual": copia o manual. Por padrão limpa o que é da máquina antiga. */
  async duplicarManual(id: string, opcoes?: {
    titulo?: string;
    projetoId?: string;
    limparEspecificos?: boolean;
  }): Promise<ApiResponse> {
    return this.request(`/manuais/${id}/duplicar`, {
      method: 'POST',
      body: JSON.stringify(opcoes ?? {}),
    });
  }

  /** Arquiva por padrão; `definitivo` apaga o registro e as imagens. */
  async removerManual(id: string, definitivo = false): Promise<ApiResponse> {
    const sufixo = definitivo ? '?definitivo=true' : '';
    return this.request(`/manuais/${id}${sufixo}`, { method: 'DELETE' });
  }

  async reativarManual(id: string): Promise<ApiResponse> {
    return this.request(`/manuais/${id}/restaurar`, { method: 'POST' });
  }

  async listarVersoesManual(id: string): Promise<ApiResponse> {
    return this.request(`/manuais/${id}/versoes`);
  }

  async buscarVersaoManual(id: string, numero: number): Promise<ApiResponse> {
    return this.request(`/manuais/${id}/versoes/${numero}`);
  }

  /** Congela o estado atual como versão. `bumpRevisao` sobe o "Rev. n" da capa. */
  async congelarVersaoManual(id: string, opcoes?: {
    motivo?: string;
    resumo?: string;
    bumpRevisao?: boolean;
    dados?: unknown;
  }): Promise<ApiResponse> {
    return this.request(`/manuais/${id}/versoes`, {
      method: 'POST',
      body: JSON.stringify(opcoes ?? {}),
    });
  }

  async restaurarVersaoManual(id: string, numero: number): Promise<ApiResponse> {
    return this.request(`/manuais/${id}/versoes/${numero}/restaurar`, { method: 'POST' });
  }

  async compararVersoesManual(id: string, de: number, para: number): Promise<ApiResponse> {
    return this.request(`/manuais/${id}/versoes/${de}/diferencas/${para}`);
  }

  /**
   * Sobe uma imagem do manual e devolve a URL. As imagens ficam fora do JSON
   * do documento — em base64 elas estouram a cota do navegador e engordam
   * cada gravação.
   */
  async enviarImagemManual(id: string, arquivo: File): Promise<ApiResponse<{ url: string; bytes: number }>> {
    const form = new FormData();
    form.append('arquivo', arquivo, arquivo.name);

    const response = await fetchApi(`${API_BASE_URL}/manuais/${id}/imagens`, {
      method: 'POST',
      body: form,
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    return await response.json();
  }

  async removerImagemManual(id: string, url: string): Promise<ApiResponse> {
    return this.request(`/manuais/${id}/imagens?url=${encodeURIComponent(url)}`, { method: 'DELETE' });
  }

  // ===================== Vendas → Cotações =====================

  async listarCotacoes(filtros?: { busca?: string; status?: string }): Promise<ApiResponse> {
    const query = new URLSearchParams();
    if (filtros?.busca) query.set('busca', filtros.busca);
    if (filtros?.status) query.set('status', filtros.status);
    const sufixo = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/cotacoes${sufixo}`);
  }

  async obterCotacao(id: string): Promise<ApiResponse> {
    return this.request(`/cotacoes/${id}`);
  }

  async criarCotacao(dados: {
    titulo: string;
    cliente?: string;
    obra?: string;
    observacoes?: string;
    validadeDias?: number;
    /** Só o admin escolhe; os demais herdam o próprio squad. */
    squadId?: string | null;
  }): Promise<ApiResponse> {
    return this.request('/cotacoes', { method: 'POST', body: JSON.stringify(dados) });
  }

  async atualizarCotacao(
    id: string,
    dados: { titulo?: string; cliente?: string; obra?: string; observacoes?: string; validadeDias?: number; status?: string }
  ): Promise<ApiResponse> {
    return this.request(`/cotacoes/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
  }

  async removerCotacao(id: string): Promise<ApiResponse> {
    return this.request(`/cotacoes/${id}`, { method: 'DELETE' });
  }

  /** ARs disponíveis na pasta do servidor, marcando as já importadas. */
  async listarArsDoServidor(cotacaoId?: string): Promise<ApiResponse> {
    const sufixo = cotacaoId ? `?cotacaoId=${encodeURIComponent(cotacaoId)}` : '';
    return this.request(`/cotacoes/ars-servidor${sufixo}`);
  }

  async importarArsDoServidor(cotacaoId: string, arquivos: string[]): Promise<ApiResponse> {
    return this.request(`/cotacoes/${cotacaoId}/importar-ar-servidor`, {
      method: 'POST',
      body: JSON.stringify({ arquivos }),
    });
  }

  /** Envia PDFs de AR do computador. O chamador é quem divide em lotes. */
  async importarArsEnviadas(cotacaoId: string, arquivos: File[]): Promise<ApiResponse> {
    const form = new FormData();
    arquivos.forEach((arquivo) => form.append('arquivos', arquivo, arquivo.name));

    const headers: Record<string, string> = {};

    const response = await fetchApi(`${API_BASE_URL}/cotacoes/${cotacaoId}/importar-ar`, {
      method: 'POST',
      headers,
      body: form,
    });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    return await response.json();
  }

  async atualizarMaquinaCotacao(
    id: string,
    dados: { nome?: string; tag?: string | null; setor?: string | null; modoValor?: string; valorUnico?: number | null; observacoes?: string | null }
  ): Promise<ApiResponse> {
    return this.request(`/cotacoes/maquinas/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
  }

  async removerMaquinaCotacao(id: string): Promise<ApiResponse> {
    return this.request(`/cotacoes/maquinas/${id}`, { method: 'DELETE' });
  }

  async atualizarParteCotacao(
    id: string,
    dados: { valor?: number | null; status?: string; observacoes?: string | null; adequada?: boolean }
  ): Promise<ApiResponse> {
    return this.request(`/cotacoes/partes/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
  }

  async atualizarPartesCotacaoEmLote(
    cotacaoId: string,
    parteIds: string[],
    dados: { valor?: number | null; status?: string }
  ): Promise<ApiResponse> {
    return this.request(`/cotacoes/${cotacaoId}/partes-lote`, {
      method: 'PUT',
      body: JSON.stringify({ parteIds, ...dados }),
    });
  }

  async obterTabelaCotacao(id: string): Promise<ApiResponse> {
    return this.request(`/cotacoes/${id}/tabela`);
  }

  /** Baixa XLSX ou PDF da cotação já com o nome sugerido pelo servidor. */
  async exportarCotacao(id: string, formato: 'xlsx' | 'pdf', opcoes?: { fotos?: boolean }): Promise<void> {
    const query = formato === 'pdf' && opcoes?.fotos === false ? '?fotos=false' : '';
    const url = `${API_BASE_URL}/cotacoes/${id}/exportar/${formato}${query}`;

    const headers: Record<string, string> = {};

    const response = await fetchApi(url, { method: 'GET', mode: 'cors', headers });

    if (response.status === 401) {
      this.invalidateActiveSession({ redirectToLogin: true, reason: 'unauthorized' });
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    if (!response.ok) {
      const erro = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      throw new Error(erro.message || 'Não foi possível exportar a cotação.');
    }

    const blob = await response.blob();
    const nome =
      extrairNomeArquivoContentDisposition(response.headers.get('Content-Disposition')) ??
      `cotacao.${formato}`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = nome;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  async removerProjeto(id: string): Promise<ApiResponse> {
    return this.request(`/projetos/${id}`, {
      method: 'DELETE',
    });
  }

  // IA - Análise de Riscos
  async analisarRiscosIA(dados: {
    imagemBase64: string;
    descricaoMaquina: string;
    parte?: string;
    descricaoCondicaoAtual?: string;
    perigosMecanicos?: string[];
    consequencias?: string[];
  }): Promise<ApiResponse> {
    return this.request('/ai/analisar-riscos', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async analisarECriarRiscosIA(
    apreciacaoId: string,
    dados: {
      imagemBase64: string;
      descricaoMaquina: string;
      parte?: string;
    }
  ): Promise<ApiResponse> {
    return this.request(`/ai/analisar-e-criar-riscos/${apreciacaoId}`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  // Empresas
  async listarEmpresas(params?: {
    page?: number;
    limit?: number;
    ativo?: boolean;
    busca?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.ativo !== undefined) queryParams.append('ativo', params.ativo.toString());
    if (params?.busca) queryParams.append('busca', params.busca);

    const query = queryParams.toString();
    return this.request(`/empresas${query ? `?${query}` : ''}`);
  }

  async buscarEmpresa(id: string): Promise<ApiResponse> {
    return this.request(`/empresas/${id}`);
  }

  async criarEmpresa(dados: { nome: string; cnpj?: string; ativo?: boolean }): Promise<ApiResponse> {
    return this.request('/empresas', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarEmpresa(id: string, dados: { nome?: string; cnpj?: string; ativo?: boolean }): Promise<ApiResponse> {
    return this.request(`/empresas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerEmpresa(id: string): Promise<ApiResponse> {
    return this.request(`/empresas/${id}`, {
      method: 'DELETE',
    });
  }

  // Squads
  async listarSquads(params?: { ativo?: boolean; busca?: string }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.ativo !== undefined) queryParams.append('ativo', params.ativo.toString());
    if (params?.busca) queryParams.append('busca', params.busca);

    const query = queryParams.toString();
    return this.request(`/squads${query ? `?${query}` : ''}`);
  }

  async buscarSquad(id: string): Promise<ApiResponse> {
    return this.request(`/squads/${id}`);
  }

  async criarSquad(dados: { nome: string; regiao?: string; descricao?: string; ativo?: boolean }): Promise<ApiResponse> {
    return this.request('/squads', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarSquad(id: string, dados: { nome?: string; regiao?: string; descricao?: string; ativo?: boolean }): Promise<ApiResponse> {
    return this.request(`/squads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  async removerSquad(id: string): Promise<ApiResponse> {
    return this.request(`/squads/${id}`, {
      method: 'DELETE',
    });
  }

  // Usuários
  async listarUsuarios(params?: {
    page?: number;
    limit?: number;
    busca?: string;
    ativo?: boolean;
    role?: string;
    squadId?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page != null) queryParams.append('page', params.page.toString());
    if (params?.limit != null) queryParams.append('limit', params.limit.toString());
    if (params?.busca) queryParams.append('busca', params.busca);
    if (params?.ativo !== undefined) queryParams.append('ativo', params.ativo.toString());
    if (params?.role) queryParams.append('role', params.role);
    if (params?.squadId) queryParams.append('squadId', params.squadId);
    const query = queryParams.toString();
    return this.request(`/users${query ? `?${query}` : ''}`);
  }

  async listarCargos(): Promise<ApiResponse<string[]>> {
    return this.request('/users/cargos');
  }

  async criarUsuario(dados: {
    nome: string;
    email: string;
    senha: string;
    role?: string;
    ativo?: boolean;
    empresaId?: string | null;
    squadId?: string | null;
    crea?: string | null;
    cargo?: string | null;
    descricao?: string | null;
    fotoUrl?: string | null;
  }): Promise<ApiResponse> {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async listarUsuariosPorEmpresa(empresaId: string, ativo?: boolean): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (ativo !== undefined) queryParams.append('ativo', ativo.toString());
    const query = queryParams.toString();
    return this.request(`/users/por-empresa/${empresaId}${query ? `?${query}` : ''}`);
  }

  async buscarUsuario(id: string): Promise<ApiResponse> {
    return this.request(`/users/${id}`);
  }

  async atualizarAssinaturaUsuario(userId: string, dados: { assinaturaUrl?: string; assinaturaDigital?: string }): Promise<ApiResponse> {
    return this.request(`/users/${userId}/assinatura`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async atualizarUsuario(id: string, dados: {
    nome?: string;
    email?: string;
    senha?: string;
    role?: string;
    ativo?: boolean;
    cargo?: string;
    crea?: string;
    descricao?: string;
    empresaId?: string;
    squadId?: string | null;
    fotoUrl?: string | null;
    assinaturaUrl?: string;
    assinaturaDigital?: string;
  }): Promise<ApiResponse> {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    });
  }

  // Fotos de máquinas (uploads do servidor)
  async listarFotosUploaded(projetoId: string): Promise<ApiResponse<UploadedFotosResponse>> {
    const query = new URLSearchParams({ projetoId }).toString();
    return this.request(`/fotos/uploaded?${query}`);
  }

  /** `legado` cria a pasta já dentro do acervo legado (revisão foto a foto). */
  async criarPastaFotos(
    projetoId: string,
    machineName: string,
    legado = false
  ): Promise<ApiResponse<{ machineFolder: string; machineName: string; legado?: boolean }>> {
    return this.request('/fotos/uploaded/folders', {
      method: 'POST',
      body: JSON.stringify({ projetoId, machineName, legado }),
    });
  }

  /**
   * Migra uma máquina do legado para as pastas atuais. Só as fotos aprovadas
   * mudam de acervo; o resto é arquivado junto com o histórico da revisão.
   */
  async migrarPastaLegado(
    projetoId: string,
    machineFolder: string
  ): Promise<ApiResponse<{ machineFolder: string; migradas: number; arquivadoEm: string }>> {
    return this.request(
      `/fotos/uploaded/${encodeURIComponent(machineFolder)}/migrar?projetoId=${encodeURIComponent(projetoId)}`,
      { method: 'POST' }
    );
  }

  /** Aprova/reprova uma foto do legado. `status: null` volta para pendente. */
  async revisarFotoDaPasta(
    projetoId: string,
    machineFolder: string,
    fileName: string,
    status: RevisaoFotoStatus | null,
    motivo?: string
  ): Promise<ApiResponse<{ nomeArquivo: string; revisao: RevisaoFoto | null }>> {
    return this.request(
      `/fotos/uploaded/${encodeURIComponent(machineFolder)}/${encodeURIComponent(fileName)}/revisao?projetoId=${encodeURIComponent(projetoId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ status, motivo }),
      }
    );
  }

  async listarFotosPorApreciacao(
    apreciacaoId: string
  ): Promise<ApiResponse<UploadedMachineFolder | null>> {
    return this.request(`/fotos/por-apreciacao/${encodeURIComponent(apreciacaoId)}`);
  }

  async encaixarFotosComIA(apreciacaoId: string): Promise<ApiResponse<EncaixeFotosResultado>> {
    return this.request(`/ai/encaixar-fotos/${encodeURIComponent(apreciacaoId)}`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Recupera o último resultado salvo do "encaixar fotos com IA" (ou null),
  // para reabrir a análise sem reprocessar.
  async obterEncaixeSalvo(
    apreciacaoId: string
  ): Promise<ApiResponse<{ resultado: EncaixeFotosResultado; geradoEm: string } | null>> {
    return this.request(`/ai/encaixar-fotos/${encodeURIComponent(apreciacaoId)}/salvo`, {
      method: 'GET',
    });
  }

  async criarApreciacaoParaPasta(
    projetoId: string,
    machineFolder: string,
    templateId?: string
  ): Promise<ApiResponse<{ apreciacaoId: string; maquinaId: string }>> {
    return this.request(
      `/fotos/uploaded/${encodeURIComponent(machineFolder)}/apreciacao?projetoId=${encodeURIComponent(projetoId)}`,
      {
        method: 'POST',
        body: JSON.stringify({ templateId }),
      }
    );
  }

  async atualizarAreaPasta(
    projetoId: string,
    machineFolder: string,
    area: string
  ): Promise<ApiResponse<{ area: string | null }>> {
    return this.request(
      `/fotos/uploaded/${encodeURIComponent(machineFolder)}/area?projetoId=${encodeURIComponent(projetoId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ area }),
      }
    );
  }

  /** Define/remove o status manual da pasta (enviar null limpa a marcação). */
  async atualizarStatusPasta(
    projetoId: string,
    machineFolder: string,
    status: string | null
  ): Promise<ApiResponse<{ status: string | null }>> {
    return this.request(
      `/fotos/uploaded/${encodeURIComponent(machineFolder)}/status?projetoId=${encodeURIComponent(projetoId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ status: status ?? '' }),
      }
    );
  }

  /** Renomeia a pasta (diretório) de fotos, preservando metadados e vínculos. */
  async renomearPastaFotos(
    projetoId: string,
    machineFolder: string,
    machineName: string
  ): Promise<ApiResponse<{ machineFolder: string; machineName: string }>> {
    return this.request(
      `/fotos/uploaded/${encodeURIComponent(machineFolder)}/renomear?projetoId=${encodeURIComponent(projetoId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ machineName }),
      }
    );
  }

  async vincularApreciacaoPasta(
    projetoId: string,
    machineFolder: string,
    apreciacaoId: string
  ): Promise<ApiResponse<{ apreciacaoIds?: string[]; apreciacaoId?: string | null }>> {
    return this.request(
      `/fotos/uploaded/${encodeURIComponent(machineFolder)}/apreciacao-link?projetoId=${encodeURIComponent(projetoId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ apreciacaoId, acao: 'add' }),
      }
    );
  }

  /**
   * Define a lista completa de apreciações vinculadas à pasta (N:N). Enviar a
   * lista efetiva preserva vínculos auto-resolvidos e permite remover qualquer um.
   */
  async definirApreciacoesPasta(
    projetoId: string,
    machineFolder: string,
    apreciacaoIds: string[]
  ): Promise<ApiResponse<{ apreciacaoIds?: string[] }>> {
    return this.request(
      `/fotos/uploaded/${encodeURIComponent(machineFolder)}/apreciacao-link?projetoId=${encodeURIComponent(projetoId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ apreciacaoIds }),
      }
    );
  }

  /** Remove TODOS os vínculos de apreciação da pasta. */
  async desvincularApreciacaoPasta(
    projetoId: string,
    machineFolder: string
  ): Promise<ApiResponse<{ apreciacaoIds?: string[] }>> {
    return this.request(
      `/fotos/uploaded/${encodeURIComponent(machineFolder)}/apreciacao-link?projetoId=${encodeURIComponent(projetoId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ apreciacaoId: '' }),
      }
    );
  }

  async uploadFotosParaPasta(
    projetoId: string,
    machineFolder: string,
    files: File[]
  ): Promise<ApiResponse<{ machineFolder: string; enviados: Array<{ nomeArquivo: string; url: string }> }>> {
    // Comprime antes de enviar: o acervo por pasta chega a centenas de fotos.
    const comprimidos = await comprimirImagens(files);

    const formData = new FormData();
    comprimidos.forEach((file) => formData.append('fotos', file));

    const headers: Record<string, string> = {};

    const response = await fetchApi(
      `${API_BASE_URL}/fotos/uploaded/${encodeURIComponent(machineFolder)}/upload?projetoId=${encodeURIComponent(projetoId)}`,
      {
        method: 'POST',
        headers,
        body: formData,
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: data.message || 'Erro ao enviar fotos',
        errors: data.errors || [],
      };
    }
    return {
      success: true,
      data: data.data,
      message: data.message || 'Fotos enviadas com sucesso',
    };
  }

  /**
   * Extrai um quadro do vídeo no instante dado e grava como foto da pasta.
   * O corte é feito com ffmpeg no servidor, então sai na resolução real do
   * vídeo — o canvas do navegador só entregaria o que o player renderizou.
   */
  async extrairFrameDeVideo(
    projetoId: string,
    machineFolder: string,
    fileName: string,
    tempoSegundos: number
  ): Promise<ApiResponse<UploadedFoto>> {
    const query = new URLSearchParams({ projetoId }).toString();
    return this.request(
      `/fotos/uploaded/${encodeURIComponent(machineFolder)}/${encodeURIComponent(fileName)}/frame?${query}`,
      { method: 'POST', body: JSON.stringify({ tempo: tempoSegundos }) }
    );
  }

  /**
   * Duração (em segundos) de cada vídeo da pasta, por nome de arquivo.
   * A revisão do legado usa isso para decidir quais vídeos vale adiantar.
   */
  async duracoesVideosPasta(
    projetoId: string,
    machineFolder: string
  ): Promise<ApiResponse<Record<string, number>>> {
    const query = new URLSearchParams({ projetoId }).toString();
    return this.request(`/fotos/uploaded/${encodeURIComponent(machineFolder)}/duracoes?${query}`);
  }

  /** Confirma a importação analisada: extrai e move as fotos para o acervo. */
  async confirmarImportacaoZip(token: string): Promise<ApiResponse<ImportacaoZipResultado>> {
    return this.request(`/fotos/uploaded/importar-zip/${encodeURIComponent(token)}/confirmar`, {
      method: 'POST',
    });
  }

  /**
   * Pergunta se um ZIP desse tamanho caberia no disco do servidor, antes de
   * começar a enviar. A recusa por disco (507) chega antes do corpo ser lido,
   * então sem essa consulta o upload simplesmente empaca em 0% sem explicação.
   */
  async verificarEspacoImportacaoZip(
    tamanhoBytes: number
  ): Promise<ApiResponse<{ cabe: boolean; livreGB: number; necessarioGB: number; mensagem: string }>> {
    return this.request(`/fotos/uploaded/importar-zip/espaco?tamanho=${Math.round(tamanhoBytes)}`);
  }

  /** Descarta um ZIP enviado que não vai ser importado (libera o disco). */
  async cancelarImportacaoZip(token: string): Promise<ApiResponse<void>> {
    return this.request(`/fotos/uploaded/importar-zip/${encodeURIComponent(token)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Etapa 1: envia o ZIP e recebe o mapa de pastas que seriam criadas, sem
   * gravar nada. Usa XHR (e não fetch) porque um acervo tem GB e o envio
   * precisa reportar progresso.
   */
  async importarZipFotos(
    projetoId: string,
    file: File,
    legado: boolean,
    onProgress?: (percentual: number) => void
  ): Promise<ApiResponse<AnaliseZip>> {
    const formData = new FormData();
    formData.append('arquivo', file);

    const query = new URLSearchParams({ projetoId, legado: legado ? '1' : '0' }).toString();

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/fotos/uploaded/importar-zip?${query}`);
      // XHR só manda o cookie de sessão com withCredentials.
      xhr.withCredentials = true;

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        const data = (() => {
          try {
            return JSON.parse(xhr.responseText);
          } catch {
            return {};
          }
        })();
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ success: true, data: data.data, message: data.message || 'ZIP importado' });
        } else {
          resolve({
            success: false,
            message: data.message || `Falha ao importar ZIP (HTTP ${xhr.status})`,
            errors: data.errors || [],
          });
        }
      };
      xhr.onerror = () => resolve({ success: false, message: 'Erro de rede ao enviar o ZIP' });
      xhr.ontimeout = () => resolve({ success: false, message: 'Tempo esgotado ao enviar o ZIP' });
      xhr.send(formData);
    });
  }

  async excluirPastaFotos(projetoId: string, machineFolder: string): Promise<ApiResponse<void>> {
    const query = new URLSearchParams({ projetoId }).toString();
    return this.request(`/fotos/uploaded/${encodeURIComponent(machineFolder)}?${query}`, {
      method: 'DELETE',
    });
  }

  async excluirFotoDaPasta(projetoId: string, machineFolder: string, fileName: string): Promise<ApiResponse<void>> {
    const query = new URLSearchParams({ projetoId }).toString();
    return this.request(`/fotos/uploaded/${encodeURIComponent(machineFolder)}/${encodeURIComponent(fileName)}?${query}`, {
      method: 'DELETE',
    });
  }

  async downloadFotosParte(apreciacaoId: string, parte: string): Promise<Blob> {
    const headers: Record<string, string> = {};

    const response = await fetchApi(
      `${API_BASE_URL}/apreciacoes/${encodeURIComponent(apreciacaoId)}/partes/${encodeURIComponent(parte)}/fotos/download`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Erro ao baixar ZIP das imagens da parte');
    }
    return response.blob();
  }

  async downloadZipPastaFotos(projetoId: string, machineFolder: string): Promise<Blob> {
    const headers: Record<string, string> = {};

    const response = await fetchApi(
      `${API_BASE_URL}/fotos/uploaded/${encodeURIComponent(machineFolder)}/download?projetoId=${encodeURIComponent(projetoId)}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Erro ao baixar ZIP da pasta');
    }
    return response.blob();
  }

  // ─────────────── Criação assistida de apreciação por IA ───────────────
  // O pipeline roda como job em background no servidor (leva minutos), então
  // a UI dispara e acompanha por polling — nunca espera a resposta.

  /**
   * Dry-run: resolve o contexto da pasta (máquina, área, TAG, entrevistado,
   * modelo) sem criar nada. Alimenta o modal de confirmação de pré-voo.
   */
  async previewApreciacaoIA(projetoId: string, machineFolder: string): Promise<ApiResponse<PreviewIA>> {
    const query = new URLSearchParams({ projetoId }).toString();
    return this.request(`/ai/apreciacao-de-pasta/${encodeURIComponent(machineFolder)}/preview?${query}`);
  }

  /** Inicia a geração. Responde 202 com o jobId; 409 se já houver uma concluída. */
  async criarApreciacaoIA(
    projetoId: string,
    machineFolder: string,
    opcoes: {
      forcar?: boolean;
      templateMultiParteId?: string | null;
      sobrescreverContexto?: OverrideContextoIA;
    } = {}
  ): Promise<ApiResponse<{ jobId: string; jaExistia: boolean; job: IaJob | null }>> {
    const query = new URLSearchParams({ projetoId }).toString();
    return this.request(`/ai/apreciacao-de-pasta/${encodeURIComponent(machineFolder)}?${query}`, {
      method: 'POST',
      body: JSON.stringify(opcoes),
    });
  }

  async obterJobIA(jobId: string): Promise<ApiResponse<IaJob>> {
    return this.request(`/ai/jobs/${encodeURIComponent(jobId)}`);
  }

  /** Situação de várias pastas numa requisição só, para os selos da grade. */
  async statusIaPorPasta(
    projetoId: string,
    machineFolders: string[]
  ): Promise<ApiResponse<{ itens: StatusIaPorPasta[] }>> {
    const query = new URLSearchParams({ projetoId, machineFolders: machineFolders.join(',') }).toString();
    return this.request(`/ai/apreciacao-de-pasta/status?${query}`);
  }

  async cancelarJobIA(jobId: string): Promise<ApiResponse<void>> {
    return this.request(`/ai/jobs/${encodeURIComponent(jobId)}/cancelar`, { method: 'POST' });
  }

  /** Retoma um job interrompido por reinício do servidor ou que falhou. */
  async retomarJobIA(jobId: string): Promise<ApiResponse<{ jobId: string }>> {
    return this.request(`/ai/jobs/${encodeURIComponent(jobId)}/retomar`, { method: 'POST' });
  }

  // Upload de imagens
  async uploadFotoPanoramica(apreciacaoId: string, file: File): Promise<ApiResponse<{ url: string; fileName: string }>> {
    const formData = new FormData();
    formData.append('file', await comprimirImagem(file));

    const headers: Record<string, string> = {};

    const response = await fetchApi(`${API_BASE_URL}/apreciacoes/${apreciacaoId}/upload-foto-panoramica`, {
      method: 'POST',
      headers,
      body: formData,
    });

    let data;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch (error) {
      console.error('Erro ao fazer parse da resposta:', error);
      return {
        success: false,
        message: 'Erro ao processar resposta do servidor',
        errors: ['Resposta inválida do servidor'],
      };
    }

    if (!response.ok) {
      return {
        success: false,
        message: data.message || 'Erro ao fazer upload da foto panorâmica',
        errors: data.errors || [],
      };
    }

    return {
      success: true,
      data: data.data,
      message: data.message || 'Foto panorâmica enviada com sucesso',
    };
  }

  async uploadFotoIdentificacao(apreciacaoId: string, file: File): Promise<ApiResponse<{ url: string; fileName: string }>> {
    const formData = new FormData();
    formData.append('file', await comprimirImagem(file));

    const headers: Record<string, string> = {};

    const response = await fetchApi(`${API_BASE_URL}/apreciacoes/${apreciacaoId}/upload-foto-identificacao`, {
      method: 'POST',
      headers,
      body: formData,
    });

    let data;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch (error) {
      console.error('Erro ao fazer parse da resposta:', error);
      return {
        success: false,
        message: 'Erro ao processar resposta do servidor',
        errors: ['Resposta inválida do servidor'],
      };
    }

    if (!response.ok) {
      return {
        success: false,
        message: data.message || 'Erro ao fazer upload da foto de identificação',
        errors: data.errors || [],
      };
    }

    return {
      success: true,
      data: data.data,
      message: data.message || 'Foto de identificação enviada com sucesso',
    };
  }

  async uploadFotoParte(apreciacaoId: string, file: File): Promise<ApiResponse<{ url: string; fileName: string }>> {
    const formData = new FormData();
    formData.append('file', await comprimirImagem(file));

    const headers: Record<string, string> = {};

    const response = await fetchApi(`${API_BASE_URL}/apreciacoes/${apreciacaoId}/upload-foto-parte`, {
      method: 'POST',
      headers,
      body: formData,
    });

    let data;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch (error) {
      console.error('Erro ao fazer parse da resposta:', error);
      return {
        success: false,
        message: 'Erro ao processar resposta do servidor',
        errors: ['Resposta inválida do servidor'],
      };
    }

    if (!response.ok) {
      return {
        success: false,
        message: data.message || 'Erro ao fazer upload da foto da parte',
        errors: data.errors || [],
      };
    }

    return {
      success: true,
      data: data.data,
      message: data.message || 'Foto da parte enviada com sucesso',
    };
  }

  // Feedback visual (comentários de correção apontados na tela)
  async listarFeedbacks(status?: 'aberto' | 'resolvido'): Promise<ApiResponse> {
    return this.request(`/feedback${status ? `?status=${status}` : ''}`);
  }

  async enviarFeedback(data: {
    pagina: string;
    seletor?: string | null;
    elemento?: string | null;
    posX?: number | null;
    posY?: number | null;
    comentario: string;
    screenshot?: string | null;
  }): Promise<ApiResponse> {
    return this.request('/feedback', { method: 'POST', body: JSON.stringify(data) });
  }

  async atualizarFeedback(id: string, data: { status?: string; comentario?: string }): Promise<ApiResponse> {
    return this.request(`/feedback/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async removerFeedback(id: string): Promise<ApiResponse> {
    return this.request(`/feedback/${id}`, { method: 'DELETE' });
  }

  // ==========================================================================
  // Revisão de apreciações (retorno comentado do cliente)
  // ==========================================================================

  /** Lê o PDF devolvido sem gravar nada — é a prévia antes de abrir o ciclo. */
  async analisarPdfRetorno(params: {
    arquivo: File;
    projetoId?: string;
    apreciacaoId?: string;
  }): Promise<ApiResponse<AnalisePdfRetorno>> {
    const form = new FormData();
    // Campos antes do arquivo: o backend só enxerga os de texto que já
    // passaram pelo parser quando chega na parte binária.
    if (params.projetoId) form.append('projetoId', params.projetoId);
    if (params.apreciacaoId) form.append('apreciacaoId', params.apreciacaoId);
    form.append('arquivo', params.arquivo, params.arquivo.name);
    return this.request('/revisoes/analisar', { method: 'POST', body: form });
  }

  /** Abre o ciclo e devolve a apreciação para "em revisão". */
  async registrarRetornoCliente(params: {
    apreciacaoId: string;
    arquivo?: File | null;
    guiaOrigemId?: string | null;
    observacoes?: string | null;
    comentarios?: Array<{ texto: string; pagina?: number; parte?: string | null }>;
  }): Promise<ApiResponse<RevisaoResumo>> {
    if (params.arquivo) {
      const form = new FormData();
      form.append('apreciacaoId', params.apreciacaoId);
      if (params.guiaOrigemId) form.append('guiaOrigemId', params.guiaOrigemId);
      if (params.observacoes) form.append('observacoes', params.observacoes);
      form.append('arquivo', params.arquivo, params.arquivo.name);
      return this.request('/revisoes', { method: 'POST', body: form });
    }
    return this.request('/revisoes', {
      method: 'POST',
      body: JSON.stringify({
        apreciacaoId: params.apreciacaoId,
        guiaOrigemId: params.guiaOrigemId ?? null,
        observacoes: params.observacoes ?? null,
        comentarios: params.comentarios ?? [],
      }),
    });
  }

  async listarRevisoes(params: {
    projetoId: string;
    status?: string;
    guiaId?: string;
    apreciacaoId?: string;
  }): Promise<ApiResponse<RevisaoResumo[]>> {
    const query = new URLSearchParams({ projetoId: params.projetoId });
    if (params.status) query.set('status', params.status);
    if (params.guiaId) query.set('guiaId', params.guiaId);
    if (params.apreciacaoId) query.set('apreciacaoId', params.apreciacaoId);
    return this.request(`/revisoes?${query.toString()}`);
  }

  async indicadoresRevisoes(projetoId: string): Promise<ApiResponse<IndicadoresRevisao>> {
    return this.request(`/revisoes/indicadores?projetoId=${encodeURIComponent(projetoId)}`);
  }

  async obterRevisao(id: string): Promise<ApiResponse<RevisaoDetalhe>> {
    return this.request(`/revisoes/${encodeURIComponent(id)}`);
  }

  /** Checklist do ciclo em aberto de uma apreciação, agrupado por parte. */
  async checklistRevisaoApreciacao(apreciacaoId: string): Promise<ApiResponse<ChecklistRevisao | null>> {
    return this.request(`/apreciacoes/${encodeURIComponent(apreciacaoId)}/checklist-revisao`);
  }

  async atualizarComentarioRevisao(
    comentarioId: string,
    dados: { status?: string; parte?: string | null; resolucao?: string | null; responsavelId?: string | null },
  ): Promise<ApiResponse> {
    return this.request(`/revisoes/comentarios/${encodeURIComponent(comentarioId)}`, {
      method: 'PATCH',
      body: JSON.stringify(dados),
    });
  }

  async adicionarComentarioRevisao(
    revisaoId: string,
    dados: { texto: string; parte?: string | null; pagina?: number | null; autor?: string | null },
  ): Promise<ApiResponse> {
    return this.request(`/revisoes/${encodeURIComponent(revisaoId)}/comentarios`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async removerComentarioRevisao(comentarioId: string): Promise<ApiResponse> {
    return this.request(`/revisoes/comentarios/${encodeURIComponent(comentarioId)}`, { method: 'DELETE' });
  }

  /** Edita o que a revisão imprime na capa (coluna DESCRIÇÃO) e as observações. */
  async atualizarRevisao(
    id: string,
    dados: { descricaoCapa?: string | null; observacoes?: string | null },
  ): Promise<ApiResponse<RevisaoResumo>> {
    return this.request(`/revisoes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(dados),
    });
  }

  async concluirRevisao(
    id: string,
    dados: { guiaRetornoId?: string | null; observacoes?: string | null; forcar?: boolean },
  ): Promise<ApiResponse<RevisaoResumo>> {
    return this.request(`/revisoes/${encodeURIComponent(id)}/concluir`, {
      method: 'POST',
      body: JSON.stringify(dados),
    });
  }

  async cancelarRevisao(id: string, motivo?: string | null): Promise<ApiResponse<RevisaoResumo>> {
    return this.request(`/revisoes/${encodeURIComponent(id)}/cancelar`, {
      method: 'POST',
      body: JSON.stringify({ motivo: motivo ?? null }),
    });
  }
}

export const apiService = new ApiService();
